import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import ExtensionRoundedIcon from '@mui/icons-material/ExtensionRounded';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import InsertChartOutlinedRoundedIcon from '@mui/icons-material/InsertChartOutlinedRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MemoryRoundedIcon from '@mui/icons-material/MemoryRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import TableRowsRoundedIcon from '@mui/icons-material/TableRowsRounded';
import TerminalRoundedIcon from '@mui/icons-material/TerminalRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';
import { copilotRemoteApi } from '../../services/copilotRemoteApi';
import { copilotSkillsApi } from '../../services/copilotSkillsApi';
import './copilot.css';

const BRAND_BLUE = PRIMARYCOLOR || '#005CC5';
const DARK_TEXT = '#0B1F3A';
const SLATE_TEXT = '#65748A';
const SUCCESS = '#16A34A';
const WARNING = '#D97706';
const DANGER = '#DC2626';
const MAX_NON_DEFAULT_SKILLS = 5;
const ACTIVE_REMOTE_COMMAND_STATUSES = ['queued', 'claimed', 'running'];
const CANCELLABLE_REMOTE_COMMAND_STATUSES = ['queued', 'claimed', 'running'];

const remoteCommandStatusMeta = {
  pending_approval: { label: 'Waiting for approval', color: WARNING },
  queued: { label: 'Queued', color: BRAND_BLUE },
  claimed: { label: 'Picked up by machine', color: BRAND_BLUE },
  running: { label: 'Running', color: BRAND_BLUE },
  completed: { label: 'Completed', color: SUCCESS },
  failed: { label: 'Failed', color: DANGER },
  rejected: { label: 'Rejected', color: DANGER },
  cancelled: { label: 'Cancelled', color: SLATE_TEXT },
  expired: { label: 'Timed out', color: WARNING },
};

const rendererLabels = {
  table: 'Table',
  stats_summary: 'Stats',
  detail_card: 'Detail',
  created_card: 'Created',
  list: 'List',
  report_section: 'Report',
  threat_model: 'Threat Model',
  infrastructure_summary: 'Infrastructure',
  company_profile_card: 'Company',
  jobs_list: 'Jobs',
  news_list: 'News',
  grouped_lists: 'Grouped',
  bar_chart: 'Chart',
  terminal_output: 'Terminal Output',
  raw_json: 'Details',
};

const eventMeta = {
  status: { label: 'Status', color: BRAND_BLUE, Icon: PendingActionsRoundedIcon },
  tool_started: { label: 'Tool started', color: BRAND_BLUE, Icon: TerminalRoundedIcon },
  tool_finished: { label: 'Tool finished', color: SUCCESS, Icon: CheckCircleRoundedIcon },
  tool_failed: { label: 'Tool failed', color: DANGER, Icon: ErrorOutlineRoundedIcon },
  confirmation_required: { label: 'Approval needed', color: WARNING, Icon: FactCheckOutlinedIcon },
  artifact_created: { label: 'Artifact created', color: BRAND_BLUE, Icon: Inventory2OutlinedIcon },
  assistant_message_final: { label: 'Assistant replied', color: SUCCESS, Icon: AutoAwesomeRoundedIcon },
  error: { label: 'Error', color: DANGER, Icon: ErrorOutlineRoundedIcon },
};

const timelineIconMeta = {
  play: { color: BRAND_BLUE, Icon: LaunchRoundedIcon },
  activity: { color: BRAND_BLUE, Icon: PendingActionsRoundedIcon },
  tool: { color: BRAND_BLUE, Icon: TerminalRoundedIcon },
  terminal: { color: BRAND_BLUE, Icon: TerminalRoundedIcon },
  check: { color: SUCCESS, Icon: CheckCircleRoundedIcon },
  success: { color: SUCCESS, Icon: CheckCircleRoundedIcon },
  approval: { color: WARNING, Icon: FactCheckOutlinedIcon },
  warning: { color: WARNING, Icon: FactCheckOutlinedIcon },
  error: { color: DANGER, Icon: ErrorOutlineRoundedIcon },
  artifact: { color: BRAND_BLUE, Icon: Inventory2OutlinedIcon },
};

const isEmptyValue = (value) => {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return String(value).trim() === '';
};

const hasValue = (value) => !isEmptyValue(value);

const asArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
};

const formatLabel = (value) =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

const truncateText = (value, max = 120) => {
  const text = String(value || '');
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}...`;
};

const formatDateTime = (value) => {
  if (!value) return 'Not yet';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown';
  return parsed.toLocaleString();
};

const getActionSummaryValue = (action, label) => {
  const summary = Array.isArray(action?.summary) ? action.summary : [];
  const match = summary.find((item) => String(item.label || '').toLowerCase() === String(label).toLowerCase());
  return match?.value;
};

const isShellCommandAction = (action) =>
  String(action?.tool_name || action?.payload?.tool_name || '').toLowerCase() === 'run_shell_command' ||
  String(action?.title || '').toLowerCase().includes('shell command') ||
  Boolean(action?.details?.command || getActionSummaryValue(action, 'Command'));

const getRemoteCommandMeta = (status) => {
  const normalized = String(status || '').toLowerCase();
  return remoteCommandStatusMeta[normalized] || { label: formatLabel(normalized || 'unknown'), color: SLATE_TEXT };
};

const isUrl = (value) => /^https?:\/\//i.test(String(value || '').trim());

const getToken = () => sessionStorage.getItem('token');

const unwrapList = (response, key) => {
  if (!response || typeof response !== 'object') return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response[key])) return response[key];
  if (Array.isArray(response.data)) return response.data;
  return [];
};

const normalizeMessage = (message) => ({
  id: message.id || `${message.role || 'message'}-${Date.now()}-${Math.random()}`,
  role: message.role || message.sender || (message.user_input ? 'user' : 'assistant'),
  content: message.content || message.message || message.response || message.user_input || '',
  created_at: message.created_at || message.createdAt || message.timestamp || '',
});

const normalizeMessageText = (value) =>
  String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();

const sameMessageContent = (a, b) =>
  String(a?.role || '').toLowerCase() === String(b?.role || '').toLowerCase() &&
  normalizeMessageText(a?.content) === normalizeMessageText(b?.content);

const appendUniqueMessages = (currentMessages, nextMessages) => {
  const next = [...currentMessages];
  nextMessages.forEach((message) => {
    const hasSameId = next.some((item) => String(item.id) === String(message.id));
    const hasSameContent = next.some((item) => sameMessageContent(item, message));
    if (!hasSameId && !hasSameContent) next.push(message);
  });
  return next;
};

const getArtifactRenderer = (artifact) =>
  artifact?.payload?.renderer || artifact?.renderer || 'raw_json';

const cleanObjectEntries = (object) =>
  Object.entries(object || {}).filter(([, value]) => hasValue(value));

const extractStreamEvents = (data) => {
  if (!data || typeof data !== 'object') return [];
  const explicitEvents = [
    ...(Array.isArray(data.events) ? data.events : []),
    ...(Array.isArray(data.stream_events) ? data.stream_events : []),
    data.event,
  ].filter(Boolean);
  const confirmationPayload =
    data.confirmation_required ||
    data.confirmation ||
    data.pending_action ||
    data.pendingAction;
  const confirmationEvents = confirmationPayload
    ? [{ id: confirmationPayload.id || confirmationPayload.action_id, event_type: 'confirmation_required', payload: confirmationPayload }]
    : [];

  return [...explicitEvents, ...confirmationEvents].map((event, index) => ({
    id: event.id || event.event_id || event.created_at || `response-event-${Date.now()}-${index}`,
    event_type: event.event_type || event.type || event.event,
    payload: event.payload || event.data || event,
  })).filter((event) => event.event_type);
};

const preferredTableColumns = [
  'name',
  'title',
  'domain',
  'host',
  'path',
  'url',
  'request_type',
  'method',
  'severity',
  'status',
  'summary',
  'description',
  'total_exploits_found',
  'count',
];

const columnRank = (column) => {
  const normalized = String(column || '').toLowerCase();
  const preferredIndex = preferredTableColumns.findIndex((preferred) => normalized === preferred || normalized.includes(preferred));
  if (preferredIndex >= 0) return preferredIndex;
  if (normalized.includes('id')) return 90;
  if (normalized.includes('created') || normalized.includes('updated') || normalized.includes('date')) return 80;
  return 60;
};

const pickReadableColumns = (columns, maxColumns = 5) => {
  const ranked = [...columns].sort((a, b) => columnRank(a) - columnRank(b));
  const selected = ranked.slice(0, maxColumns);
  return columns.filter((column) => selected.includes(column));
};

const getSkillKey = (skill) => String(skill?.key ?? skill?.id ?? skill?.slug ?? '');

const getSkillIdentity = (skill) => `${skill?.source || 'system'}:${getSkillKey(skill)}`;

const isGeneralSkill = (skill) =>
  String(skill?.source || '').toLowerCase() === 'system' &&
  ['general', 'default'].includes(String(skill?.slug || skill?.key || '').toLowerCase());

const isSystemSkill = (skill) => String(skill?.source || '').toLowerCase() === 'system';

const isCustomSkill = (skill) => String(skill?.source || '').toLowerCase() === 'custom';

const normalizeSkillDetail = (skill, fallback = {}) => ({
  ...fallback,
  ...(skill || {}),
  source: skill?.source || fallback?.source || 'custom',
  key: String(skill?.key ?? skill?.id ?? fallback?.key ?? fallback?.id ?? fallback?.slug ?? ''),
});

const createEmptySkillDraft = () => ({
  id: null,
  name: '',
  description: '',
  slug: '',
  content: '# Purpose\nDescribe what this skill helps Copilot do.\n\n# Guidance\n- Add specific workflow instructions.\n- Include when Copilot should use this skill.\n',
});

function AuthError({ message }) {
  return (
    <Box className="copilot-page">
      <Card className="copilot-empty-card">
        <CardContent>
          <Typography variant="h5" sx={{ fontWeight: 900, color: DARK_TEXT }}>
            Copilot unavailable
          </Typography>
          <Typography sx={{ mt: 1, color: SLATE_TEXT }}>{message}</Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

function EmptyState({ title, description, icon }) {
  return (
    <Box className="copilot-empty-state">
      <Avatar className="copilot-empty-icon">{icon || <AutoAwesomeRoundedIcon />}</Avatar>
      <Typography sx={{ mt: 1.5, fontWeight: 900, color: DARK_TEXT }}>{title}</Typography>
      {description ? (
        <Typography variant="body2" sx={{ mt: 0.6, color: SLATE_TEXT, lineHeight: 1.6 }}>
          {description}
        </Typography>
      ) : null}
    </Box>
  );
}

function SessionListContent({ sessions, activeSessionId, loading, onSelect, onNewSession, onRefresh }) {
  return (
    <Box className="copilot-session-list-content">
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography className="copilot-workspace-section-title">Copilot Sessions</Typography>
          <Typography className="copilot-workspace-copy">Switch active chats or start a fresh workflow.</Typography>
        </Box>
        <Tooltip title="Refresh sessions" arrow>
          <IconButton size="small" onClick={onRefresh} className="copilot-session-refresh-button">
            <RefreshRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Button
        fullWidth
        startIcon={<AddCircleOutlineIcon />}
        variant="contained"
        onClick={onNewSession}
        className="copilot-primary-button copilot-new-session-button"
        sx={{ mt: 1.2 }}
      >
        New Session
      </Button>
      <Divider sx={{ my: 1.15 }} />
      <Stack spacing={0.75}>
        {loading ? (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : null}
        {!loading && !sessions.length ? (
          <EmptyState
            title="No sessions yet"
            description="Start a workspace and ask the Copilot to query pentests, summarize findings, or create a scan."
            icon={<HistoryRoundedIcon />}
          />
        ) : null}
        {sessions.map((session) => {
          const selected = session.id === activeSessionId;
          return (
            <Button
              key={session.id}
              className={`copilot-session-button ${selected ? 'is-active' : ''}`}
              onClick={() => onSelect(session.id)}
            >
              <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                <Typography className="copilot-session-title">
                  {session.title || `Session ${session.id}`}
                </Typography>
                <Typography className="copilot-session-meta">
                  {session.status || 'active'} · #{session.id}
                </Typography>
              </Box>
            </Button>
          );
        })}
      </Stack>
    </Box>
  );
}

function MessageBubble({ message }) {
  const isUser = ['user', 'human'].includes(String(message.role || '').toLowerCase());
  return (
    <Box className={`copilot-message-row ${isUser ? 'is-user' : 'is-assistant'}`}>
      {!isUser ? (
        <Avatar className="copilot-assistant-avatar">
          <PsychologyRoundedIcon fontSize="small" />
        </Avatar>
      ) : null}
      <Box className={`copilot-message ${isUser ? 'is-user' : 'is-assistant'}`}>
        <Typography className="copilot-message-label">{isUser ? 'You' : 'Pentest Copilot'}</Typography>
        <Box className="copilot-markdown">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {message.content || 'Working on it...'}
          </ReactMarkdown>
        </Box>
      </Box>
    </Box>
  );
}

function ShellCommandApprovalCard({ action, onApprove, onReject, busyActionId }) {
  const actionId = action?.id;
  const busy = String(busyActionId) === String(actionId);
  const details = action?.details || action?.payload?.details || {};
  const command = details.command || getActionSummaryValue(action, 'Command') || '';
  const machine = details.machine_name || getActionSummaryValue(action, 'Machine') || 'Selected remote machine';
  const workingDirectory = details.working_directory || getActionSummaryValue(action, 'Working Directory') || 'Default directory';
  const timeout = details.timeout_seconds || getActionSummaryValue(action, 'Timeout') || 'Default';
  const commandJobId = details.command_job_id || getActionSummaryValue(action, 'Command Job ID');

  return (
    <Card className="copilot-confirm-card copilot-shell-approval-card">
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar className="copilot-shell-avatar">
            <TerminalRoundedIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950, color: DARK_TEXT }}>
              {action?.title || 'Run Shell Command'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: SLATE_TEXT }}>
              {action?.description || 'Copilot wants approval before running this command on a remote machine.'}
            </Typography>
            <Box className="copilot-shell-meta-grid">
              <Box>
                <Typography className="copilot-kv-label">Machine</Typography>
                <Typography className="copilot-kv-value">{machine}</Typography>
              </Box>
              <Box>
                <Typography className="copilot-kv-label">Working Directory</Typography>
                <Typography className="copilot-kv-value">{workingDirectory}</Typography>
              </Box>
              <Box>
                <Typography className="copilot-kv-label">Timeout</Typography>
                <Typography className="copilot-kv-value">{String(timeout).includes('second') ? timeout : `${timeout} seconds`}</Typography>
              </Box>
              {commandJobId ? (
                <Box>
                  <Typography className="copilot-kv-label">Command Job</Typography>
                  <Typography className="copilot-kv-value">#{commandJobId}</Typography>
                </Box>
              ) : null}
            </Box>
            <Box className="copilot-terminal-command-block">
              <Typography className="copilot-terminal-title">Command</Typography>
              <pre>{command || '[Command not provided]'}</pre>
            </Box>
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
              <Button
                variant="contained"
                startIcon={busy ? <CircularProgress color="inherit" size={16} /> : <TerminalRoundedIcon />}
                disabled={busy || actionId === undefined || actionId === null || actionId === ''}
                onClick={() => onApprove(actionId)}
                className="copilot-primary-button"
              >
                {busy ? 'Waiting for machine...' : action?.confirm_label || 'Run Command'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CloseRoundedIcon />}
                disabled={busy || actionId === undefined || actionId === null || actionId === ''}
                onClick={() => onReject(actionId)}
                sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 900 }}
              >
                {action?.reject_label || 'Cancel'}
              </Button>
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ConfirmationCard({ action, onApprove, onReject, busyActionId }) {
  if (isShellCommandAction(action)) {
    return (
      <ShellCommandApprovalCard
        action={action}
        onApprove={onApprove}
        onReject={onReject}
        busyActionId={busyActionId}
      />
    );
  }

  const actionId = action?.id;
  const busy = String(busyActionId) === String(actionId);
  const hasActionId = actionId !== undefined && actionId !== null && actionId !== '';
  const summary = Array.isArray(action?.summary) ? action.summary : [];

  return (
    <Card className="copilot-confirm-card">
      <CardContent>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar sx={{ bgcolor: alpha(WARNING, 0.12), color: WARNING }}>
            <FactCheckOutlinedIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950, color: DARK_TEXT }}>
              {action?.title || (action?.tool_name ? formatLabel(action.tool_name) : 'Confirm Copilot Action')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: SLATE_TEXT }}>
              {action?.description || 'The Copilot wants approval before running this action.'}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1.2, flexWrap: 'wrap', rowGap: 1 }}>
              {action?.tool_name ? <Chip size="small" label={`Action: ${formatLabel(action.tool_name)}`} /> : null}
              <Chip size="small" label={`Status: ${formatLabel(action?.status || 'pending')}`} />
              {hasActionId ? <Chip size="small" label={`Approval ID: ${actionId}`} /> : null}
            </Stack>
            <Box className="copilot-confirm-details">
              {summary.length ? summary.map((item, index) => (
                <Box key={`${item.label || 'summary'}-${index}`} className="copilot-kv-row">
                  <Typography className="copilot-kv-label">{item.label || `Item ${index + 1}`}</Typography>
                  <SmartValue value={item.value} />
                </Box>
              )) : (
                <Typography variant="body2" sx={{ color: SLATE_TEXT }}>
                  No action summary was provided by the Copilot service.
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
              <Button
                variant="contained"
                startIcon={busy ? <CircularProgress color="inherit" size={16} /> : <CheckCircleRoundedIcon />}
                disabled={busy || !hasActionId}
                onClick={() => onApprove(actionId)}
                className="copilot-primary-button"
              >
                {action?.confirm_label || 'Confirm'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CloseRoundedIcon />}
                disabled={busy || !hasActionId}
                onClick={() => onReject(actionId)}
                sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 900 }}
              >
                {action?.reject_label || 'Reject'}
              </Button>
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function SkillSourceBadge({ skill }) {
  const system = isSystemSkill(skill);
  return (
    <Chip
      size="small"
      icon={system ? <LockOutlinedIcon /> : <EditNoteRoundedIcon />}
      label={system ? 'System' : 'Custom'}
      className={system ? 'copilot-skill-source-chip is-system' : 'copilot-skill-source-chip is-custom'}
    />
  );
}

function ActiveSkillStrip({ skills, loading, onOpenSkills }) {
  const visibleSkills = skills.slice(0, 6);
  return (
    <Box className="copilot-active-skills">
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography className="copilot-active-skills-label">Active skills</Typography>
        {loading ? (
          <Chip size="small" icon={<CircularProgress size={13} />} label="Loading" className="copilot-skill-chip" />
        ) : null}
        {!loading && !visibleSkills.length ? (
          <Chip size="small" icon={<ExtensionRoundedIcon />} label="General" className="copilot-skill-chip is-locked" />
        ) : null}
        {!loading && visibleSkills.map((skill) => (
          <Chip
            key={getSkillIdentity(skill)}
            size="small"
            icon={skill.locked || isGeneralSkill(skill) ? <LockOutlinedIcon /> : <ExtensionRoundedIcon />}
            label={skill.name || skill.slug || getSkillKey(skill)}
            className={skill.locked || isGeneralSkill(skill) ? 'copilot-skill-chip is-locked' : 'copilot-skill-chip'}
          />
        ))}
        {!loading && skills.length > visibleSkills.length ? (
          <Chip size="small" label={`+${skills.length - visibleSkills.length}`} className="copilot-skill-chip" />
        ) : null}
      </Stack>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ExtensionRoundedIcon />}
        onClick={onOpenSkills}
        className="copilot-skills-button"
      >
        Manage Skills
      </Button>
    </Box>
  );
}

function SkillCard({
  skill,
  active,
  assignDisabled,
  assigning,
  removing,
  onAssign,
  onRemove,
  onView,
  onEdit,
  onDelete,
}) {
  const locked = skill.locked || isGeneralSkill(skill);
  const editable = Boolean(skill.editable && isCustomSkill(skill));
  return (
    <Card className={`copilot-skill-card ${active ? 'is-active' : ''}`}>
      <CardContent>
        <Stack direction="row" spacing={1.2} alignItems="flex-start">
          <Avatar className={isSystemSkill(skill) ? 'copilot-skill-avatar is-system' : 'copilot-skill-avatar is-custom'}>
            {locked ? <LockOutlinedIcon /> : <ExtensionRoundedIcon />}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography className="copilot-skill-card-title">{skill.name || 'Untitled Skill'}</Typography>
              {active ? <Chip size="small" label="Active" className="copilot-skill-active-chip" /> : null}
            </Stack>
            <Typography className="copilot-skill-card-copy">
              {truncateText(skill.description || 'No description provided.', 170)}
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.3 }}>
              <SkillSourceBadge skill={skill} />
              {skill.status ? <Chip size="small" label={formatLabel(skill.status)} className="copilot-skill-muted-chip" /> : null}
              {skill.slug ? <Chip size="small" label={skill.slug} className="copilot-skill-muted-chip" /> : null}
            </Stack>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.7 }}>
          <Button size="small" variant="outlined" startIcon={<VisibilityOutlinedIcon />} onClick={() => onView(skill)} className="copilot-mini-secondary">
            View
          </Button>
          {active && !locked ? (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={removing ? <CircularProgress size={14} color="inherit" /> : <CloseRoundedIcon />}
              disabled={removing}
              onClick={() => onRemove(skill)}
              className="copilot-mini-secondary"
            >
              Remove
            </Button>
          ) : null}
          {!active ? (
            <Button
              size="small"
              variant="contained"
              startIcon={assigning ? <CircularProgress size={14} color="inherit" /> : <AddCircleOutlineIcon />}
              disabled={assignDisabled || assigning}
              onClick={() => onAssign(skill)}
              className="copilot-mini-primary"
            >
              Assign
            </Button>
          ) : null}
          {editable ? (
            <Button size="small" variant="outlined" startIcon={<EditNoteRoundedIcon />} onClick={() => onEdit(skill)} className="copilot-mini-secondary">
              Edit
            </Button>
          ) : null}
          {editable ? (
            <Button size="small" variant="outlined" color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={() => onDelete(skill)} className="copilot-mini-secondary">
              Delete
            </Button>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}

function SkillDetailPanel({ skill, loading }) {
  if (loading) {
    return (
      <Box className="copilot-skill-detail">
        <CircularProgress size={24} />
        <Typography className="copilot-workspace-copy">Loading skill content...</Typography>
      </Box>
    );
  }

  if (!skill) {
    return (
      <Box className="copilot-skill-detail is-empty">
        <Avatar className="copilot-empty-icon"><ExtensionRoundedIcon /></Avatar>
        <Typography className="copilot-workspace-title">Select a skill</Typography>
        <Typography className="copilot-workspace-copy">
          View system instructions or edit a custom team-owned skill.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="copilot-skill-detail">
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography className="copilot-skill-detail-title">{skill.name || 'Untitled Skill'}</Typography>
        <SkillSourceBadge skill={skill} />
        {skill.locked ? <Chip size="small" icon={<LockOutlinedIcon />} label="View only" className="copilot-skill-muted-chip" /> : null}
      </Stack>
      {skill.description ? (
        <Typography className="copilot-skill-card-copy" sx={{ mt: 1 }}>
          {skill.description}
        </Typography>
      ) : null}
      <Box className="copilot-skill-markdown">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {skill.content || 'No skill content available.'}
        </ReactMarkdown>
      </Box>
    </Box>
  );
}

function SkillEditor({ draft, mode, saving, onChange, onSave, onCancel }) {
  return (
    <Box className="copilot-skill-editor">
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Box>
          <Typography className="copilot-eyebrow">{mode === 'edit' ? 'Edit Custom Skill' : 'New Custom Skill'}</Typography>
          <Typography className="copilot-panel-title">{mode === 'edit' ? draft.name || 'Custom Skill' : 'Create a Copilot skill'}</Typography>
        </Box>
        <Button size="small" variant="outlined" onClick={onCancel} className="copilot-mini-secondary">
          Cancel
        </Button>
      </Stack>
      <Grid container spacing={1.2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={7}>
          <TextField
            fullWidth
            label="Skill name"
            value={draft.name}
            onChange={(event) => onChange({ ...draft, name: event.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            label="Slug"
            placeholder="auto-generated if blank"
            value={draft.slug || ''}
            onChange={(event) => onChange({ ...draft, slug: event.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            value={draft.description}
            onChange={(event) => onChange({ ...draft, description: event.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={11}
            label="Markdown skill body"
            value={draft.content}
            onChange={(event) => onChange({ ...draft, content: event.target.value })}
            className="copilot-skill-content-input"
          />
        </Grid>
      </Grid>
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
          disabled={saving || !draft.name.trim() || !draft.content.trim()}
          onClick={onSave}
          className="copilot-primary-button"
        >
          {mode === 'edit' ? 'Save Skill' : 'Create Skill'}
        </Button>
      </Stack>
    </Box>
  );
}

function SkillsDrawer({
  open,
  onClose,
  activeSessionId,
  availableSkills,
  sessionSkills,
  loadingSkills,
  loadingSessionSkills,
  selectedSkill,
  skillDetailLoading,
  skillDraft,
  skillEditorMode,
  savingSkill,
  assigningSkillKey,
  removingAssignmentId,
  deletingSkillId,
  onRefresh,
  onViewSkill,
  onNewSkill,
  onEditSkill,
  onDraftChange,
  onSaveSkill,
  onCancelEdit,
  onDeleteSkill,
  onAssignSkill,
  onRemoveSkill,
}) {
  const [search, setSearch] = useState('');
  const activeNonDefaultCount = sessionSkills.filter((skill) => !skill.locked && !isGeneralSkill(skill)).length;
  const filteredSkills = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return availableSkills;
    return availableSkills.filter((skill) =>
      [skill.name, skill.description, skill.slug, skill.source]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [availableSkills, search]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ className: 'copilot-skills-drawer' }}
    >
      <Box className="copilot-activity-drawer-header">
        <Box>
          <Typography className="copilot-eyebrow">Copilot Skills</Typography>
          <Typography className="copilot-panel-title">Session Skill Manager</Typography>
          <Typography className="copilot-workspace-copy">
            Load team playbooks into this chat so Copilot behaves with the right specialty.
          </Typography>
        </Box>
        <IconButton onClick={onClose}><CloseRoundedIcon /></IconButton>
      </Box>
      <Box className="copilot-skills-drawer-body">
        <Box className="copilot-skills-summary">
          <Box>
            <Typography className="copilot-workspace-section-title">Active in this session</Typography>
            <Typography className="copilot-workspace-copy">
              {activeNonDefaultCount} of {MAX_NON_DEFAULT_SKILLS} non-default skills loaded.
            </Typography>
          </Box>
          <Button size="small" variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={onRefresh} className="copilot-mini-secondary">
            Refresh
          </Button>
        </Box>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {loadingSessionSkills ? <Chip size="small" icon={<CircularProgress size={13} />} label="Loading active skills" /> : null}
          {!loadingSessionSkills && sessionSkills.map((skill) => (
            <Chip
              key={getSkillIdentity(skill)}
              icon={skill.locked || isGeneralSkill(skill) ? <LockOutlinedIcon /> : <ExtensionRoundedIcon />}
              label={skill.name || getSkillKey(skill)}
              onDelete={!skill.locked && !isGeneralSkill(skill) ? () => onRemoveSkill(skill) : undefined}
              className={skill.locked || isGeneralSkill(skill) ? 'copilot-skill-chip is-locked' : 'copilot-skill-chip'}
            />
          ))}
        </Stack>

        <Box className="copilot-skills-toolbar">
          <TextField
            size="small"
            fullWidth
            placeholder="Search skills..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{ startAdornment: <SearchRoundedIcon fontSize="small" sx={{ mr: 1, color: SLATE_TEXT }} /> }}
          />
          <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={onNewSkill} className="copilot-primary-button">
            New Skill
          </Button>
        </Box>

        {skillEditorMode ? (
          <SkillEditor
            draft={skillDraft}
            mode={skillEditorMode}
            saving={savingSkill}
            onChange={onDraftChange}
            onSave={onSaveSkill}
            onCancel={onCancelEdit}
          />
        ) : null}

        <Grid container spacing={1.5}>
          <Grid item xs={12} lg={6.5}>
            <Box className="copilot-skills-section-card">
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Box>
                  <Typography className="copilot-workspace-section-title">Skill Library</Typography>
                  <Typography className="copilot-workspace-copy">
                    System skills are view-only. Custom skills can be edited by your team.
                  </Typography>
                </Box>
                <Chip size="small" label={loadingSkills ? 'Loading' : `${filteredSkills.length} skills`} />
              </Stack>
              <Stack spacing={1}>
                {loadingSkills ? (
                  <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>
                ) : null}
                {!loadingSkills && !filteredSkills.length ? (
                  <Box className="copilot-workspace-empty">No skills match that search.</Box>
                ) : null}
                {!loadingSkills && filteredSkills.map((skill) => {
                  const identity = getSkillIdentity(skill);
                  const activeSkill = sessionSkills.find((item) => getSkillIdentity(item) === identity);
                  const active = Boolean(activeSkill);
                  const cardSkill = activeSkill ? { ...skill, ...activeSkill } : skill;
                  const assigning = String(assigningSkillKey) === identity;
                  const removing = activeSkill && String(removingAssignmentId) === String(activeSkill.assignment_id);
                  const overLimit = !active && activeNonDefaultCount >= MAX_NON_DEFAULT_SKILLS;
                  return (
                    <SkillCard
                      key={identity}
                      skill={cardSkill}
                      active={active}
                      assignDisabled={!activeSessionId || overLimit}
                      assigning={assigning}
                      removing={removing}
                      onAssign={onAssignSkill}
                      onRemove={onRemoveSkill}
                      onView={onViewSkill}
                      onEdit={onEditSkill}
                      onDelete={onDeleteSkill}
                    />
                  );
                })}
              </Stack>
            </Box>
          </Grid>
          <Grid item xs={12} lg={5.5}>
            <Box className="copilot-skills-section-card is-sticky">
              <SkillDetailPanel skill={selectedSkill} loading={skillDetailLoading} />
              {deletingSkillId ? (
                <Typography className="copilot-workspace-copy" sx={{ mt: 1 }}>
                  Deleting custom skill...
                </Typography>
              ) : null}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Drawer>
  );
}

function ChatThread({
  messages,
  pendingActions,
  events,
  liveTimelineRun,
  connected,
  sessionSkills,
  loadingSessionSkills,
  activityOpenCount,
  isProcessing,
  onOpenActivity,
  onOpenSkills,
  onSend,
  sending,
  onApprove,
  onReject,
  busyActionId,
}) {
  const [input, setInput] = useState('');
  const scrollRef = useRef(null);
  const latestActivityEvent = useMemo(
    () => [...events].reverse().find((event) => !['assistant_message_final', 'confirmation_required'].includes(event.event_type)),
    [events]
  );

  useEffect(() => {
    const scrollNode = scrollRef.current;
    if (!scrollNode) return;
    window.requestAnimationFrame(() => {
      scrollNode.scrollTo({
        top: scrollNode.scrollHeight,
        behavior: 'smooth',
      });
    });
  }, [messages.length, pendingActions.length, latestActivityEvent?.id, liveTimelineRun?.items?.length, liveTimelineRun?.state, isProcessing]);

  const submit = (event) => {
    event?.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || sending) return;
    onSend(trimmed);
    setInput('');
  };

  return (
    <Card className="copilot-panel copilot-chat-panel">
      <Box className="copilot-chat-header">
        <Box>
          <Typography className="copilot-eyebrow">Pentesting Copilot</Typography>
          <Typography className="copilot-chat-title">Ask, investigate, create, summarize</Typography>
        </Box>
        <Chip
          icon={<BoltRoundedIcon />}
          label={connected ? 'Streaming live' : 'Ready'}
          sx={{ bgcolor: alpha(BRAND_BLUE, 0.08), color: BRAND_BLUE, fontWeight: 900 }}
        />
        <Button
          variant="outlined"
          startIcon={<HistoryRoundedIcon />}
          onClick={onOpenActivity}
          sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 900 }}
        >
          Activity {activityOpenCount ? `(${activityOpenCount})` : ''}
        </Button>
      </Box>
      <ActiveSkillStrip
        skills={sessionSkills}
        loading={loadingSessionSkills}
        onOpenSkills={onOpenSkills}
      />

      <Box className={`copilot-chat-scroll ${!messages.length && !pendingActions.length ? 'is-empty' : ''}`} ref={scrollRef}>
        {!messages.length && !pendingActions.length ? (
          <EmptyState
            title="Start with an operational question"
            description="Try: Show me the web endpoints for pentest 123, summarize OSINT stats, or create an OSINT pentest for example.com."
            icon={<PsychologyRoundedIcon />}
          />
        ) : null}
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
        {pendingActions.map((action) => (
          <ConfirmationCard
            key={action.id}
            action={action}
            onApprove={onApprove}
            onReject={onReject}
            busyActionId={busyActionId}
          />
        ))}
        {(isProcessing || sending) ? (
          <InlineActivity
            event={latestActivityEvent}
            events={events}
            liveTimelineRun={liveTimelineRun}
            sending={sending}
          />
        ) : null}
      </Box>

      <Box component="form" className="copilot-composer" onSubmit={submit}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="Ask the Copilot to inspect pentests, query findings, create a scan, or assemble a report section..."
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              submit(event);
            }
          }}
          className="copilot-input"
        />
        <Button
          type="submit"
          variant="contained"
          endIcon={sending ? <CircularProgress color="inherit" size={18} /> : <SendRoundedIcon />}
          disabled={sending || !input.trim()}
          className="copilot-send-button"
        >
          Send
        </Button>
      </Box>
    </Card>
  );
}

function InlineActivity({ event, events = [], liveTimelineRun = null, sending }) {
  const activeRun = liveTimelineRun;
  const activeItems = asArray(activeRun?.items).map(normalizeTimelineItem).filter((item) => !item.collapse);
  const currentTimelineItem = activeItems[activeItems.length - 1];
  const meta = currentTimelineItem ? getTimelineMeta(currentTimelineItem) : event ? eventMeta[event.event_type] || eventMeta.status : eventMeta.status;
  const Icon = meta.Icon;
  const display = currentTimelineItem ? {
    title: currentTimelineItem.title,
    detail: currentTimelineItem.message,
  } : event ? getEventDisplay(event) : {
    title: 'Starting',
    detail: sending ? 'Waiting for the agent to start...' : 'Working on your request...',
  };
  const recentTimelineSteps = activeItems.slice(-5);
  const recentEventSteps = [...events]
    .reverse()
    .filter((item) => !['assistant_message_final', 'confirmation_required'].includes(item.event_type))
    .slice(0, 3)
    .reverse();
  const recentSteps = recentTimelineSteps.length ? recentTimelineSteps : recentEventSteps;

  return (
    <Box className="copilot-inline-activity">
      <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.2 }}>
        <Avatar className="copilot-assistant-avatar">
          <AutoAwesomeRoundedIcon fontSize="small" />
        </Avatar>
        <Box>
          <Typography className="copilot-message-label">Copilot activity</Typography>
          <Typography sx={{ color: DARK_TEXT, fontWeight: 900 }}>
            {display.title}
          </Typography>
        </Box>
      </Stack>
      <Box className="copilot-inline-event">
        <Box className="copilot-thinking-orb" sx={{ '--copilot-thinking-color': meta.color }}>
          {(currentTimelineItem || event) ? <Icon fontSize="small" /> : <CircularProgress size={16} />}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography className="copilot-timeline-title">{display.title}</Typography>
            <Box className="copilot-thinking-dots" aria-label="Copilot is thinking">
              <span />
              <span />
              <span />
            </Box>
          </Stack>
          <Typography className="copilot-timeline-copy">{truncateText(display.detail, 110)}</Typography>
        </Box>
      </Box>
      {recentSteps.length ? (
        <Stack spacing={0.8} sx={{ mt: 1.2 }}>
          {recentSteps.map((step, index) => {
            const isTimelineStep = Boolean(step.phase || step.run_id || step.message);
            const stepMeta = isTimelineStep ? getTimelineMeta(step) : eventMeta[step.event_type] || eventMeta.status;
            const display = isTimelineStep
              ? { title: step.title, detail: step.message }
              : getEventDisplay(step);
            return (
              <Box key={`${step.id || step.phase || 'step'}-${step.event_type || index}`} className="copilot-inline-step">
                <Box sx={{ bgcolor: stepMeta.color }} />
                <Typography>{display.title}</Typography>
                <Typography>{truncateText(display.detail, 88)}</Typography>
              </Box>
            );
          })}
        </Stack>
      ) : null}
    </Box>
  );
}

const getArtifactTitle = (artifact) => {
  const renderer = getArtifactRenderer(artifact);
  const genericTitles = new Set(['details', 'detail', 'raw', 'raw json', 'artifact', 'copilot artifact']);
  const title = String(artifact?.title || '').trim();
  if (title && !genericTitles.has(title.toLowerCase())) return title;
  if (artifact?.artifact_type) return formatLabel(artifact.artifact_type);
  return rendererLabels[renderer] || formatLabel(renderer) || 'Copilot Output';
};

// eslint-disable-next-line no-unused-vars -- reserved for the artifact drawer when structured outputs return to the operator panel.
const getArtifactSummary = (artifact) => {
  const renderer = getArtifactRenderer(artifact);
  const payload = artifact?.payload || {};
  const rows = asArray(payload.rows || payload.items || payload.data || payload.list);
  if (rows.length) return `${rows.length} ${rows.length === 1 ? 'record' : 'records'}`;
  if (payload.summary && typeof payload.summary === 'string') return truncateText(payload.summary, 120);
  if (payload.description) return truncateText(payload.description, 120);
  if (artifact?.artifact_type) return rendererLabels[renderer] || formatLabel(artifact.artifact_type);
  const keys = cleanObjectEntries(payload).filter(([key]) => key !== 'renderer').map(([key]) => formatLabel(key));
  if (keys.length) return keys.slice(0, 3).join(', ');
  return rendererLabels[renderer] || 'Structured output';
};

const getTimelineFromEvent = (event) => {
  const payload = event?.payload || {};
  const timeline = payload.timeline || {};
  if (!timeline || isEmptyValue(timeline)) return null;
  return {
    id: event.id,
    event_type: event.event_type,
    run_id: timeline.run_id || payload.run_id,
    state: timeline.state || payload.state,
    phase: timeline.phase || payload.phase,
    title: timeline.title || payload.title,
    message: timeline.message || payload.message,
    level: timeline.level || payload.level || 'info',
    display: timeline.display || 'timeline',
    icon: timeline.icon || payload.icon || 'activity',
    collapse: Boolean(timeline.collapse),
    created_at: event.created_at || payload.created_at,
  };
};

const normalizeTimelineItem = (item) => ({
  id: item.id,
  event_type: item.event_type,
  run_id: item.run_id,
  state: item.state,
  phase: item.phase,
  title: item.title || formatLabel(item.phase || item.event_type || 'Activity'),
  message: item.message || 'Copilot activity',
  level: item.level || 'info',
  display: item.display || 'timeline',
  icon: item.icon || 'activity',
  collapse: Boolean(item.collapse),
  created_at: item.created_at,
});

const isPendingRunId = (runId) => String(runId || '').startsWith('pending-') || String(runId || '').startsWith('approval-');

const mergeTimelineItems = (currentItems, nextItems) => {
  const merged = [...asArray(currentItems)];
  asArray(nextItems).forEach((nextItem) => {
    const normalized = normalizeTimelineItem(nextItem);
    const existingIndex = merged.findIndex((item) => (
      String(item.id || '') === String(normalized.id || '') &&
      String(item.event_type || '') === String(normalized.event_type || '')
    ));
    if (existingIndex >= 0) {
      merged[existingIndex] = { ...merged[existingIndex], ...normalized };
    } else {
      merged.push(normalized);
    }
  });
  return merged;
};

const mergeTimelineRun = (currentRun, nextRun) => {
  if (!nextRun) return currentRun;
  if (!currentRun) return { ...nextRun, items: asArray(nextRun.items).map(normalizeTimelineItem) };
  const currentRunId = currentRun.run_id;
  const nextRunId = nextRun.run_id || currentRunId;
  return {
    ...currentRun,
    ...nextRun,
    run_id: isPendingRunId(currentRunId) ? nextRunId : currentRunId || nextRunId,
    title: nextRun.title || currentRun.title || 'Copilot Run',
    state: nextRun.state || currentRun.state || 'running',
    started_at: currentRun.started_at || nextRun.started_at,
    updated_at: nextRun.updated_at || currentRun.updated_at || new Date().toISOString(),
    items: mergeTimelineItems(currentRun.items, nextRun.items),
  };
};

const createLiveRun = ({ runId, title, itemTitle, message, icon = 'activity', eventType = 'status' }) => {
  const now = new Date().toISOString();
  return {
    run_id: runId || `pending-${Date.now()}`,
    title,
    state: 'running',
    started_at: now,
    updated_at: now,
    items: [{
      id: `${runId || 'pending'}-${Date.now()}`,
      event_type: eventType,
      state: 'running',
      phase: 'queued',
      title: itemTitle,
      message,
      level: 'info',
      display: 'timeline',
      icon,
      collapse: false,
      created_at: now,
    }],
  };
};

const getTimelineMeta = (item) => {
  const byIcon = timelineIconMeta[item?.icon];
  if (byIcon) return byIcon;
  const byEvent = eventMeta[item?.event_type];
  if (byEvent) return { color: byEvent.color, Icon: byEvent.Icon };
  if (item?.level === 'error') return timelineIconMeta.error;
  if (item?.level === 'warning') return timelineIconMeta.warning;
  if (item?.state === 'completed') return timelineIconMeta.success;
  if (item?.state === 'waiting_for_approval') return timelineIconMeta.approval;
  return timelineIconMeta.activity;
};

const getEventDisplay = (event) => {
  const payload = event?.payload || {};
  const timeline = getTimelineFromEvent(event);
  if (timeline) {
    return {
      title: timeline.title || formatLabel(timeline.phase || event?.event_type || 'Activity'),
      detail: timeline.message || payload.message || 'Copilot activity',
      timeline,
    };
  }
  const type = event?.event_type;

  if (type === 'status') {
    return { title: 'Status', detail: payload.message || payload.status || 'Copilot is working' };
  }
  if (type === 'tool_started') {
    return { title: 'Tool started', detail: payload.tool_name ? `Running ${formatLabel(payload.tool_name)}` : 'Running tool' };
  }
  if (type === 'tool_finished') {
    return { title: 'Tool finished', detail: payload.tool_name ? `${formatLabel(payload.tool_name)} completed` : 'Tool completed' };
  }
  if (type === 'tool_failed') {
    return { title: 'Tool failed', detail: payload.message || payload.tool_name || 'Tool failed' };
  }
  if (type === 'confirmation_required') {
    return { title: 'Approval needed', detail: payload.title || payload.description || 'Review pending action' };
  }
  if (type === 'action_approved') {
    return { title: 'Action approved', detail: payload.title || payload.tool_name || 'Approved action is running' };
  }
  if (type === 'action_rejected') {
    return { title: 'Action rejected', detail: payload.title || payload.tool_name || 'Action was cancelled' };
  }
  if (type === 'artifact_created') {
    const artifact = payload.artifact || payload;
    return { title: 'Artifact created', detail: getArtifactTitle(artifact) };
  }
  if (type === 'assistant_message_final') {
    return { title: 'Assistant replied', detail: 'Final response received' };
  }
  if (type === 'error') {
    return { title: 'Error', detail: payload.message || 'Copilot reported an error' };
  }

  return {
    title: eventMeta[type]?.label || formatLabel(type || 'Activity'),
    detail: payload.message || payload.title || payload.tool_name || payload.artifact_type || 'Copilot activity',
  };
};

// eslint-disable-next-line no-unused-vars -- reserved for terminal/command stream cards when command tools are enabled.
const getEventPrimary = (event) => getEventDisplay(event).detail;

function TimelineRunCard({ run, compact = false }) {
  const visibleItems = asArray(run?.items)
    .map(normalizeTimelineItem)
    .filter((item) => compact ? !item.collapse : true)
    .slice(compact ? -4 : 0);
  const state = run?.state || visibleItems[visibleItems.length - 1]?.state || 'running';

  return (
    <Box className={`copilot-run-card ${compact ? 'is-compact' : ''}`}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography className="copilot-run-title">
            {run?.title || visibleItems[0]?.title || 'Copilot Run'}
          </Typography>
          <Typography className="copilot-workspace-copy">
            {run?.updated_at ? `Updated ${run.updated_at}` : run?.run_id || 'Grouped activity'}
          </Typography>
        </Box>
        <Chip size="small" label={formatLabel(state)} />
      </Stack>
      <Stack spacing={0.8} sx={{ mt: 1.2 }}>
        {visibleItems.length ? visibleItems.map((item, index) => {
          const meta = getTimelineMeta(item);
          const Icon = meta.Icon;
          return (
            <Box key={item.id || `${item.phase}-${index}`} className="copilot-run-item">
              <Avatar sx={{ bgcolor: alpha(meta.color, 0.11), color: meta.color, width: 28, height: 28 }}>
                <Icon fontSize="small" />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography className="copilot-workspace-title">{item.title}</Typography>
                <Typography className="copilot-workspace-copy">{truncateText(item.message, compact ? 92 : 150)}</Typography>
              </Box>
            </Box>
          );
        }) : (
          <Box className="copilot-workspace-empty">No timeline items yet.</Box>
        )}
      </Stack>
    </Box>
  );
}

const platformToolCards = [
  {
    title: 'Pentest Data',
    description: 'Query assessments, scope, assets, endpoints, findings, and status.',
    Icon: Inventory2OutlinedIcon,
  },
  {
    title: 'Create Scans',
    description: 'Start supported pentest workflows with approval when needed.',
    Icon: LaunchRoundedIcon,
  },
  {
    title: 'Findings Review',
    description: 'Summarize issues, prioritize risk, and explain remediation paths.',
    Icon: FactCheckOutlinedIcon,
  },
  {
    title: 'Reports',
    description: 'Draft summaries, report sections, and client-ready deliverables.',
    Icon: InsertChartOutlinedRoundedIcon,
  },
];

function OperatorSkillCard({ skill }) {
  const locked = skill.locked || isGeneralSkill(skill);
  const sourceLabel = isSystemSkill(skill) ? 'System' : 'Custom';

  return (
    <Box className="copilot-operator-skill-card">
      <Avatar className={locked ? 'copilot-operator-context-icon is-locked' : 'copilot-operator-context-icon'}>
        {locked ? <LockOutlinedIcon fontSize="small" /> : <ExtensionRoundedIcon fontSize="small" />}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography className="copilot-workspace-title">{skill.name || skill.slug || getSkillKey(skill)}</Typography>
        <Typography className="copilot-workspace-copy">
          {sourceLabel}{locked ? ' - locked' : skill.status ? ` - ${formatLabel(skill.status)}` : ''}
        </Typography>
      </Box>
    </Box>
  );
}

function ToolCapabilityCard({ title, description, Icon, status = 'Available', actionLabel, onAction }) {
  return (
    <Box className="copilot-tool-card">
      <Avatar className="copilot-operator-context-icon">
        <Icon fontSize="small" />
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography className="copilot-workspace-title">{title}</Typography>
          <Chip size="small" label={status} className="copilot-tool-status-chip" />
        </Stack>
        <Typography className="copilot-workspace-copy">{description}</Typography>
        {actionLabel && onAction ? (
          <Button size="small" variant="outlined" onClick={onAction} className="copilot-mini-secondary" sx={{ mt: 1 }}>
            {actionLabel}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}

function RemoteCommandStatusChip({ status }) {
  const meta = getRemoteCommandMeta(status);
  return (
    <Chip
      size="small"
      label={meta.label}
      sx={{
        bgcolor: alpha(meta.color, 0.1),
        color: meta.color,
        border: `1px solid ${alpha(meta.color, 0.18)}`,
        fontWeight: 900,
      }}
    />
  );
}

function SessionRemoteMachinePanel({
  machines,
  machineDraft,
  sessionMachine,
  loading,
  saving,
  onDraftChange,
  onSave,
  onClear,
  onOpenMachines,
}) {
  const selectedMachine = machines.find((machine) => String(machine.id) === String(machineDraft.machine_id));
  return (
    <Box className="copilot-workspace-section">
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography className="copilot-workspace-section-title">Remote Machine</Typography>
          <Typography className="copilot-workspace-copy">
            Required before Copilot can run shell commands.
          </Typography>
        </Box>
        <Button size="small" onClick={onOpenMachines} className="copilot-mini-secondary">
          Manage
        </Button>
      </Stack>
      <Stack spacing={1.1} sx={{ mt: 1.2 }}>
        {loading ? <Box className="copilot-workspace-empty">Loading machine setting...</Box> : null}
        {!loading && sessionMachine?.machine ? (
          <Box className="copilot-operator-skill-card">
            <Avatar className="copilot-operator-context-icon"><MemoryRoundedIcon fontSize="small" /></Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography className="copilot-workspace-title">{sessionMachine.machine.name || `Machine ${sessionMachine.machine_id}`}</Typography>
              <Typography className="copilot-workspace-copy">
                {formatLabel(sessionMachine.machine.status || 'unknown')} - {sessionMachine.working_directory || 'Default directory'}
              </Typography>
            </Box>
          </Box>
        ) : null}
        <TextField
          select
          size="small"
          label="Machine"
          value={machineDraft.machine_id || ''}
          onChange={(event) => onDraftChange({ ...machineDraft, machine_id: event.target.value })}
          fullWidth
        >
          <MenuItem value="">Select a machine</MenuItem>
          {machines.filter((machine) => String(machine.status || '').toLowerCase() !== 'archived').map((machine) => (
            <MenuItem key={machine.id} value={machine.id}>
              {machine.name || `Machine ${machine.id}`} ({formatLabel(machine.status || 'unknown')})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Working directory"
          value={machineDraft.working_directory}
          onChange={(event) => onDraftChange({ ...machineDraft, working_directory: event.target.value })}
          fullWidth
        />
        <TextField
          size="small"
          type="number"
          label="Timeout seconds"
          value={machineDraft.timeout_seconds}
          onChange={(event) => onDraftChange({ ...machineDraft, timeout_seconds: event.target.value })}
          fullWidth
        />
        {selectedMachine?.machine_key ? (
          <Box className="copilot-machine-key-mini">
            <KeyRoundedIcon fontSize="small" />
            <Typography>{selectedMachine.machine_key}</Typography>
            <IconButton size="small" onClick={() => navigator.clipboard?.writeText(selectedMachine.machine_key)}>
              <ContentCopyRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : null}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="contained"
            disabled={saving || !machineDraft.machine_id}
            onClick={onSave}
            className="copilot-mini-primary"
          >
            {saving ? 'Saving...' : 'Save Machine'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={saving || !sessionMachine}
            onClick={onClear}
            className="copilot-mini-secondary"
          >
            Clear
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

function RemoteCommandsPanel({ commands, loading, cancellingCommandId, onRefresh, onOpenCommand, onCancelCommand }) {
  const shownCommands = commands.slice(0, 4);
  return (
    <Box className="copilot-workspace-section">
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography className="copilot-workspace-section-title">Remote Commands</Typography>
          <Typography className="copilot-workspace-copy">
            Shell jobs created by approved Copilot actions.
          </Typography>
        </Box>
        <Button size="small" onClick={onRefresh} className="copilot-mini-secondary">
          Refresh
        </Button>
      </Stack>
      <Stack spacing={1} sx={{ mt: 1.2 }}>
        {loading ? <Box className="copilot-workspace-empty">Loading commands...</Box> : null}
        {!loading && shownCommands.length ? shownCommands.map((command) => {
          const cancellable = CANCELLABLE_REMOTE_COMMAND_STATUSES.includes(String(command.status || '').toLowerCase());
          const cancelling = String(cancellingCommandId) === String(command.id);
          return (
            <Box key={command.id} className="copilot-remote-command-card">
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography className="copilot-workspace-title">{truncateText(command.command, 92)}</Typography>
                  <Typography className="copilot-workspace-copy">
                    {command.machine?.name || `Machine ${command.machine_id || 'unknown'}`} - Exit {command.exit_code ?? 'pending'}
                  </Typography>
                </Box>
                <RemoteCommandStatusChip status={command.status} />
              </Stack>
              <Typography className="copilot-workspace-copy" sx={{ mt: 0.7 }}>
                Created {formatDateTime(command.created_at)}
              </Typography>
              <Stack direction="row" spacing={0.8} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <Button size="small" variant="outlined" onClick={() => onOpenCommand(command.id)} className="copilot-mini-secondary">
                  Details
                </Button>
                {cancellable ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={cancelling}
                    onClick={() => onCancelCommand(command.id)}
                    className="copilot-mini-secondary"
                  >
                    {cancelling ? 'Cancelling...' : 'Cancel'}
                  </Button>
                ) : null}
              </Stack>
            </Box>
          );
        }) : null}
        {!loading && !shownCommands.length ? (
          <Box className="copilot-workspace-empty">No remote commands for this session yet.</Box>
        ) : null}
        {!loading && commands.length > shownCommands.length ? (
          <Box className="copilot-workspace-empty">{commands.length - shownCommands.length} more commands available.</Box>
        ) : null}
      </Stack>
    </Box>
  );
}

function WorkspacePanel({
  connected,
  sessions,
  activeSessionId,
  loadingSessions,
  sessionSkills,
  loadingSessionSkills,
  machines,
  sessionMachine,
  machineDraft,
  loadingSessionMachine,
  savingSessionMachine,
  remoteCommands,
  loadingRemoteCommands,
  cancellingCommandId,
  onSelectSession,
  onNewSession,
  onRefreshSessions,
  onOpenSkills,
  onMachineDraftChange,
  onSaveSessionMachine,
  onClearSessionMachine,
  onOpenMachines,
  onRefreshRemoteCommands,
  onOpenCommand,
  onCancelCommand,
}) {
  const [activeTab, setActiveTab] = useState('sessions');
  const visibleSkills = sessionSkills.slice(0, 5);
  const contextTabs = [
    { key: 'sessions', label: 'Sessions', Icon: HistoryRoundedIcon },
    { key: 'profile', label: 'Profile & Tools', Icon: ExtensionRoundedIcon },
    { key: 'mcp', label: 'MCP', Icon: Inventory2OutlinedIcon },
    { key: 'remote', label: 'Remote Machine', Icon: MemoryRoundedIcon },
  ];

  return (
    <Card className="copilot-panel copilot-workspace-panel">
      <Box className="copilot-panel-header is-compact">
        <Box>
          <Typography className="copilot-eyebrow">Operator Context</Typography>
          <Typography className="copilot-panel-title">Control Surface</Typography>
        </Box>
        <Chip
          size="small"
          label={connected ? 'Live' : 'Idle'}
          sx={{
            bgcolor: alpha(connected ? SUCCESS : SLATE_TEXT, 0.1),
            color: connected ? SUCCESS : SLATE_TEXT,
            fontWeight: 900,
          }}
        />
      </Box>

      <Box className="copilot-workspace-body">
        <Box className="copilot-context-tabs">
          {contextTabs.map(({ key, label, Icon }) => (
            <Button
              key={key}
              size="small"
              startIcon={<Icon fontSize="small" />}
              onClick={() => setActiveTab(key)}
              className={`copilot-context-tab ${activeTab === key ? 'is-active' : ''}`}
            >
              {label}
            </Button>
          ))}
        </Box>

        {activeTab === 'sessions' ? (
          <Box className="copilot-workspace-section">
            <SessionListContent
              sessions={sessions}
              activeSessionId={activeSessionId}
              loading={loadingSessions}
              onSelect={onSelectSession}
              onNewSession={onNewSession}
              onRefresh={onRefreshSessions}
            />
          </Box>
        ) : null}

        {activeTab === 'profile' ? (
          <>
            <Box className="copilot-workspace-section">
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography className="copilot-workspace-section-title">Active Skills</Typography>
                  <Typography className="copilot-workspace-copy">
                    Instruction playbooks loaded into this session.
                  </Typography>
                </Box>
                <Button size="small" onClick={onOpenSkills} className="copilot-mini-secondary">
                  Manage
                </Button>
              </Stack>
              <Stack spacing={1} sx={{ mt: 1.2 }}>
                {loadingSessionSkills ? (
                  <Box className="copilot-workspace-empty">Loading active skills...</Box>
                ) : null}
                {!loadingSessionSkills && visibleSkills.length ? visibleSkills.map((skill) => (
                  <OperatorSkillCard key={getSkillIdentity(skill)} skill={skill} />
                )) : null}
                {!loadingSessionSkills && !visibleSkills.length ? (
                  <OperatorSkillCard skill={{ source: 'system', key: 'general', name: 'General Pentest Copilot', locked: true }} />
                ) : null}
                {!loadingSessionSkills && sessionSkills.length > visibleSkills.length ? (
                  <Box className="copilot-workspace-empty">{sessionSkills.length - visibleSkills.length} more skills loaded. Open Manage Skills to review all.</Box>
                ) : null}
              </Stack>
            </Box>

            <Box className="copilot-workspace-section">
              <Typography className="copilot-workspace-section-title">Platform Tools</Typography>
              <Typography className="copilot-workspace-copy">
                Built-in capabilities Copilot can call during a workflow.
              </Typography>
              <Stack spacing={0.9} sx={{ mt: 1.2 }}>
                {platformToolCards.map((tool) => (
                  <ToolCapabilityCard key={tool.title} {...tool} />
                ))}
              </Stack>
            </Box>
          </>
        ) : null}

        {activeTab === 'mcp' ? (
          <Box className="copilot-workspace-section">
            <Typography className="copilot-workspace-section-title">MCP Tools</Typography>
            <Typography className="copilot-workspace-copy">
              External tool connectors will live here once they are available for Copilot.
            </Typography>
            <Box className="copilot-workspace-empty" sx={{ mt: 1.2 }}>
              No MCP tools connected yet.
            </Box>
          </Box>
        ) : null}

        {activeTab === 'remote' ? (
          <>
            <SessionRemoteMachinePanel
              machines={machines}
              machineDraft={machineDraft}
              sessionMachine={sessionMachine}
              loading={loadingSessionMachine}
              saving={savingSessionMachine}
              onDraftChange={onMachineDraftChange}
              onSave={onSaveSessionMachine}
              onClear={onClearSessionMachine}
              onOpenMachines={onOpenMachines}
            />

            <RemoteCommandsPanel
              commands={remoteCommands}
              loading={loadingRemoteCommands}
              cancellingCommandId={cancellingCommandId}
              onRefresh={onRefreshRemoteCommands}
              onOpenCommand={onOpenCommand}
              onCancelCommand={onCancelCommand}
            />
          </>
        ) : null}
      </Box>
    </Card>
  );
}

function ActivityDrawer({ open, events, timelineRuns = [], connected, onClose }) {
  const shownEvents = [...events].reverse();
  const shownRuns = [...timelineRuns].sort((a, b) => String(b.updated_at || '').localeCompare(String(a.updated_at || '')));

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ className: 'copilot-activity-drawer' }}
    >
      <Box className="copilot-activity-drawer-header">
        <Box>
          <Typography className="copilot-eyebrow">Activity</Typography>
          <Typography className="copilot-panel-title">Tool Timeline</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            label={connected ? 'Streaming' : 'Idle'}
            sx={{
              bgcolor: alpha(connected ? SUCCESS : SLATE_TEXT, 0.1),
              color: connected ? SUCCESS : SLATE_TEXT,
              fontWeight: 900,
            }}
          />
          <IconButton onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </Box>
      <Box className="copilot-activity-drawer-body">
        {!shownRuns.length && !shownEvents.length ? (
          <EmptyState
            title="No tool activity yet"
            description="Tool calls, approvals, generated artifacts, and final messages will appear here."
            icon={<HistoryRoundedIcon />}
          />
        ) : null}
        {shownRuns.length ? (
          <Stack spacing={1.2}>
            {shownRuns.map((run) => (
              <TimelineRunCard key={run.run_id || run.id || run.title} run={run} />
            ))}
          </Stack>
        ) : (
          <Stack spacing={1}>
            {shownEvents.map((event) => {
            const meta = eventMeta[event.event_type] || eventMeta.status;
            const Icon = meta.Icon;
            const display = getEventDisplay(event);
            return (
              <Box key={`${event.id}-${event.event_type}`} className="copilot-timeline-item">
                <Avatar sx={{ bgcolor: alpha(meta.color, 0.11), color: meta.color, width: 34, height: 34 }}>
                  <Icon fontSize="small" />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography className="copilot-timeline-title">{display.title}</Typography>
                  <Typography className="copilot-timeline-copy">{truncateText(display.detail, 110)}</Typography>
                </Box>
              </Box>
            );
            })}
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}

function RemoteCommandDetailDrawer({ open, command, loading, onClose, onCancel }) {
  const cancellable = CANCELLABLE_REMOTE_COMMAND_STATUSES.includes(String(command?.status || '').toLowerCase());
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ className: 'copilot-table-detail-drawer' }}
    >
      <Box className="copilot-activity-drawer-header">
        <Box>
          <Typography className="copilot-eyebrow">Remote Command</Typography>
          <Typography className="copilot-panel-title">Command Detail</Typography>
        </Box>
        <IconButton onClick={onClose}><CloseRoundedIcon /></IconButton>
      </Box>
      <Box className="copilot-activity-drawer-body">
        {loading ? (
          <Box className="copilot-workspace-empty">Loading command detail...</Box>
        ) : null}
        {!loading && command ? (
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Box>
                <Typography className="copilot-workspace-title">{command.machine?.name || `Machine ${command.machine_id || 'unknown'}`}</Typography>
                <Typography className="copilot-workspace-copy">{command.working_directory || 'Default directory'}</Typography>
              </Box>
              <RemoteCommandStatusChip status={command.status} />
            </Stack>
            <Box className="copilot-detail-grid">
              {[
                ['Timeout', `${command.timeout_seconds || 'Default'} seconds`],
                ['Exit Code', command.exit_code ?? 'Pending'],
                ['Created', formatDateTime(command.created_at)],
                ['Approved', formatDateTime(command.approved_at)],
                ['Started', formatDateTime(command.started_at)],
                ['Completed', formatDateTime(command.completed_at)],
              ].map(([label, value]) => (
                <Box key={label} className="copilot-detail-row">
                  <Typography className="copilot-kv-label">{label}</Typography>
                  <Typography className="copilot-kv-value">{value}</Typography>
                </Box>
              ))}
            </Box>
            <Box className="copilot-terminal-command-block">
              <Typography className="copilot-terminal-title">Command</Typography>
              <pre>{command.command || '[Command not provided]'}</pre>
            </Box>
            <TerminalOutputSections payload={command} />
            {command.error_message ? (
              <Box className="copilot-terminal-command-block is-error">
                <Typography className="copilot-terminal-title">Error Message</Typography>
                <pre>{command.error_message}</pre>
              </Box>
            ) : null}
            {cancellable ? (
              <Button variant="outlined" color="error" onClick={() => onCancel(command.id)} className="copilot-mini-secondary">
                Cancel Command
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Box>
    </Drawer>
  );
}

function SmartValue({ value, compact = false }) {
  if (isEmptyValue(value)) return <Typography sx={{ color: SLATE_TEXT }}>Unknown</Typography>;
  if (isUrl(value)) {
    return (
      <Link href={value} target="_blank" rel="noopener noreferrer" className="copilot-cell-link">
        {truncateText(value, compact ? 52 : 72)}
      </Link>
    );
  }
  if (typeof value === 'boolean') return <Chip size="small" label={value ? 'Yes' : 'No'} />;
  if (typeof value === 'number' || typeof value === 'string') {
    return (
      <Typography className={compact ? 'copilot-cell-text is-compact' : 'copilot-cell-text'}>
        {String(value)}
      </Typography>
    );
  }
  if (Array.isArray(value)) {
    return (
      <Stack direction="row" spacing={0.7} sx={{ flexWrap: 'wrap', rowGap: 0.7 }}>
        {value.slice(0, compact ? 3 : 8).map((item, index) => (
          <Chip key={`${String(item)}-${index}`} size="small" label={typeof item === 'object' ? formatLabel(item.name || item.title || `Item ${index + 1}`) : String(item)} />
        ))}
        {value.length > (compact ? 3 : 8) ? <Chip size="small" label={`+${value.length - (compact ? 3 : 8)} more`} /> : null}
      </Stack>
    );
  }
  if (compact) {
    const entries = cleanObjectEntries(value);
    return (
      <Stack direction="row" spacing={0.7} sx={{ flexWrap: 'wrap', rowGap: 0.7 }}>
        {entries.slice(0, 3).map(([key, entryValue]) => (
          <Chip
            key={key}
            size="small"
            label={`${formatLabel(key)}: ${truncateText(typeof entryValue === 'object' ? JSON.stringify(entryValue) : entryValue, 28)}`}
          />
        ))}
        {entries.length > 3 ? <Chip size="small" label={`+${entries.length - 3} more`} /> : null}
      </Stack>
    );
  }
  return (
    <Box className="copilot-object-grid">
      {cleanObjectEntries(value).slice(0, 8).map(([key, entryValue]) => (
        <Box key={key} className="copilot-kv-row">
          <Typography className="copilot-kv-label">{formatLabel(key)}</Typography>
          <SmartValue value={entryValue} />
        </Box>
      ))}
    </Box>
  );
}

function TableRowDetailDrawer({ open, row, columns, onClose }) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ className: 'copilot-table-detail-drawer' }}
    >
      <Box className="copilot-activity-drawer-header">
        <Box>
          <Typography className="copilot-eyebrow">Artifact Row</Typography>
          <Typography className="copilot-panel-title">Full Record</Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>
      <Box className="copilot-activity-drawer-body">
        <Box className="copilot-detail-grid">
          {columns.map((column) => (
            <Box key={column} className="copilot-detail-row">
              <Typography className="copilot-kv-label">{formatLabel(column)}</Typography>
              <SmartValue value={row?.[column]} />
            </Box>
          ))}
        </Box>
      </Box>
    </Drawer>
  );
}

function ArtifactTable({ artifact }) {
  const payload = artifact.payload || {};
  const columns = asArray(payload.columns);
  const rows = asArray(payload.rows);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedRow, setSelectedRow] = useState(null);
  const visibleColumns = useMemo(() => pickReadableColumns(columns, 5), [columns]);
  const hiddenColumns = columns.filter((column) => !visibleColumns.includes(column));
  const visibleRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (!columns.length || !rows.length) {
    return <EmptyState title="No rows returned" description="The Copilot returned a table artifact, but there are no records to display yet." icon={<TableRowsRoundedIcon />} />;
  }

  return (
    <>
      <Box className="copilot-table-meta">
        <Chip size="small" label={`${rows.length} row${rows.length === 1 ? '' : 's'}`} />
        <Chip size="small" label={`${columns.length} column${columns.length === 1 ? '' : 's'}`} />
        {hiddenColumns.length ? <Chip size="small" label={`${hiddenColumns.length} fields in row details`} /> : null}
      </Box>
      <TableContainer component={Paper} variant="outlined" className="copilot-artifact-table">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => (
                <TableCell key={column}>{formatLabel(column)}</TableCell>
              ))}
              {hiddenColumns.length ? <TableCell align="right">Details</TableCell> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, rowIndex) => (
              <TableRow key={`${artifact.id}-${rowIndex}`}>
                {visibleColumns.map((column) => (
                  <TableCell key={column} className="copilot-compact-cell">
                    <SmartValue value={row?.[column]} compact />
                  </TableCell>
                ))}
                {hiddenColumns.length ? (
                  <TableCell align="right" className="copilot-details-cell">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSelectedRow(row)}
                      sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 900 }}
                    >
                      View
                    </Button>
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length > rowsPerPage ? (
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      ) : null}
      <TableRowDetailDrawer
        open={Boolean(selectedRow)}
        row={selectedRow}
        columns={columns}
        onClose={() => setSelectedRow(null)}
      />
    </>
  );
}

function StatsSummary({ artifact }) {
  const payload = artifact.payload || {};
  const stats = payload.stats || payload.items || payload.data || payload;
  const entries = cleanObjectEntries(stats).filter(([key]) => !['renderer', 'title', 'columns', 'rows'].includes(key));

  if (!entries.length) return <EmptyState title="No stats returned" description="There was no summary data in this artifact." icon={<InsertChartOutlinedRoundedIcon />} />;

  return (
    <Grid container spacing={1.4}>
      {entries.slice(0, 12).map(([key, value]) => (
        <Grid key={key} item xs={12} sm={6} md={4}>
          <Box className="copilot-stat-card">
            <Typography className="copilot-stat-label">{formatLabel(key)}</Typography>
            <Typography className="copilot-stat-value">
              {typeof value === 'object' ? Array.isArray(value) ? value.length : cleanObjectEntries(value).length : String(value)}
            </Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
}

function DetailCard({ artifact }) {
  const payload = artifact.payload || {};
  const data = payload.data || payload.details || payload;
  const entries = cleanObjectEntries(data).filter(([key]) => key !== 'renderer');

  return (
    <Box className="copilot-detail-grid">
      {entries.map(([key, value]) => (
        <Box key={key} className="copilot-detail-row">
          <Typography className="copilot-kv-label">{formatLabel(key)}</Typography>
          <SmartValue value={value} />
        </Box>
      ))}
    </Box>
  );
}

function CreatedCard({ artifact }) {
  const navigate = useNavigate();
  const payload = artifact.payload || {};
  const data = payload.data || payload.pentest || payload;
  const pentestId = data.pentestId || data.pentest_id || data.id;
  const type = String(data.type || data.pentest_type || '').toLowerCase();
  const route = (() => {
    if (!pentestId) return '';
    if (type.includes('osint')) return `/pentest/osint/dashboard/${pentestId}`;
    if (type.includes('dark')) return `/pentest/darkweb/dashboard/${pentestId}`;
    if (type.includes('vishing')) return `/pentest/vishing/dashboard/${pentestId}`;
    if (type.includes('api')) return `/pentest/api/dashboard/${pentestId}`;
    if (type.includes('web')) return `/pentest/web/dashboard/${pentestId}`;
    return `/pentest/dashboard/${pentestId}`;
  })();

  return (
    <Box className="copilot-created-card">
      <Avatar sx={{ bgcolor: alpha(SUCCESS, 0.12), color: SUCCESS }}>
        <CheckCircleRoundedIcon />
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 950, color: DARK_TEXT }}>
          {payload.title || artifact.title || 'Pentest Created'}
        </Typography>
        <Typography variant="body2" sx={{ color: SLATE_TEXT, mt: 0.5 }}>
          {data.name || data.scope || `Pentest #${pentestId || 'created'}`}
        </Typography>
      </Box>
      {route ? (
        <Button
          variant="outlined"
          endIcon={<LaunchRoundedIcon />}
          onClick={() => navigate(route)}
          sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 900 }}
        >
          Open
        </Button>
      ) : null}
    </Box>
  );
}

function ListArtifact({ artifact }) {
  const payload = artifact.payload || {};
  const items = asArray(payload.items || payload.rows || payload.data || payload.list);
  if (!items.length) return <EmptyState title="No items returned" description="This list artifact is empty." />;

  return (
    <Stack spacing={1}>
      {items.slice(0, 10).map((item, index) => (
        <Box key={index} className="copilot-list-item">
          {typeof item === 'object' ? (
            <>
              <Typography sx={{ fontWeight: 900, color: DARK_TEXT }}>
                {item.title || item.name || item.company || `Item ${index + 1}`}
              </Typography>
              <Typography variant="body2" sx={{ color: SLATE_TEXT, mt: 0.4 }}>
                {truncateText(item.description || item.summary || item.message || JSON.stringify(item), 180)}
              </Typography>
            </>
          ) : (
            <Typography sx={{ color: DARK_TEXT }}>{String(item)}</Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
}

function ReportSection({ artifact }) {
  const payload = artifact.payload || {};
  const content = payload.content || payload.markdown || payload.text || artifact.title || '';
  return (
    <Paper variant="outlined" className="copilot-report-section">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </Paper>
  );
}

function BarChartArtifact({ artifact }) {
  const payload = artifact.payload || {};
  const data = asArray(payload.data || payload.rows);
  const xKey = payload.xKey || payload.x_key || payload.label_key || 'label';
  const yKey = payload.yKey || payload.y_key || payload.value_key || 'value';

  if (!data.length) return <EmptyState title="No chart data returned" description="The chart artifact did not include any data points." icon={<InsertChartOutlinedRoundedIcon />} />;

  return (
    <Box sx={{ width: '100%', height: 260 }}>
      <ResponsiveContainer>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(BRAND_BLUE, 0.12)} />
          <XAxis dataKey={xKey} tick={{ fontSize: 12, fill: SLATE_TEXT }} />
          <YAxis tick={{ fontSize: 12, fill: SLATE_TEXT }} />
          <ChartTooltip />
          <Bar dataKey={yKey} fill={BRAND_BLUE} radius={[8, 8, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

function GroupedLists({ artifact }) {
  const payload = artifact.payload || {};
  const groups = payload.groups || payload.data || payload;
  const entries = cleanObjectEntries(groups).filter(([key]) => key !== 'renderer');

  return (
    <Grid container spacing={1.4}>
      {entries.map(([key, value]) => {
        const items = asArray(value);
        return (
          <Grid key={key} item xs={12} md={6}>
            <Box className="copilot-group-card">
              <Typography sx={{ fontWeight: 950, color: DARK_TEXT }}>{formatLabel(key)}</Typography>
              <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
                {items.length ? items.slice(0, 12).map((item, index) => (
                  <Chip key={index} size="small" label={typeof item === 'object' ? item.name || item.title || `Item ${index + 1}` : String(item)} />
                )) : <Chip size="small" label="None found" />}
              </Stack>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}

function RawJsonArtifact({ artifact }) {
  const payload = artifact.payload || artifact;
  const entries = cleanObjectEntries(payload).filter(([key]) => key !== 'renderer');

  if (!entries.length) {
    return <EmptyState title="No displayable fields" description="This artifact did not include fields that can be rendered." />;
  }

  return (
    <Box className="copilot-detail-grid">
      {entries.map(([key, value]) => (
        <Box key={key} className="copilot-detail-row">
          <Typography className="copilot-kv-label">{formatLabel(key)}</Typography>
          <SmartValue value={value} />
        </Box>
      ))}
    </Box>
  );
}

function TerminalOutputSections({ payload }) {
  const outputSections = [
    ['Combined', payload.combined_output],
    ['Stdout', payload.stdout],
    ['Stderr', payload.stderr],
    ['Error', payload.error_message],
  ].filter(([, value]) => hasValue(value));

  if (!outputSections.length) {
    return <Box className="copilot-workspace-empty">No command output returned yet.</Box>;
  }

  return (
    <Stack spacing={1}>
      {outputSections.map(([label, value]) => (
        <Box key={label} className={label === 'Error' || label === 'Stderr' ? 'copilot-terminal-command-block is-error' : 'copilot-terminal-command-block'}>
          <Typography className="copilot-terminal-title">{label}</Typography>
          <pre>{String(value || '')}</pre>
        </Box>
      ))}
    </Stack>
  );
}

function TerminalOutputArtifact({ artifact }) {
  const payload = artifact.payload || {};
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Box>
          <Typography sx={{ fontWeight: 950, color: DARK_TEXT }}>{artifact.title || 'Shell Command Result'}</Typography>
          <Typography variant="body2" sx={{ color: SLATE_TEXT }}>{payload.machine_name || 'Remote machine'}</Typography>
        </Box>
        <RemoteCommandStatusChip status={payload.status} />
      </Stack>
      <Box className="copilot-detail-grid">
        {[
          ['Exit Code', payload.exit_code ?? 'Pending'],
          ['Working Directory', payload.working_directory || 'Default'],
          ['Timeout', `${payload.timeout_seconds || 'Default'} seconds`],
          ['Completed', formatDateTime(payload.completed_at)],
        ].map(([label, value]) => (
          <Box key={label} className="copilot-detail-row">
            <Typography className="copilot-kv-label">{label}</Typography>
            <Typography className="copilot-kv-value">{value}</Typography>
          </Box>
        ))}
      </Box>
      <Box className="copilot-terminal-command-block">
        <Typography className="copilot-terminal-title">Command</Typography>
        <pre>{payload.command || '[Command not provided]'}</pre>
      </Box>
      <TerminalOutputSections payload={payload} />
    </Stack>
  );
}

// eslint-disable-next-line no-unused-vars -- reserved for the artifact drawer when structured outputs return to the operator panel.
function ArtifactBody({ artifact }) {
  const renderer = getArtifactRenderer(artifact);
  if (renderer === 'terminal_output') return <TerminalOutputArtifact artifact={artifact} />;
  if (renderer === 'table') return <ArtifactTable artifact={artifact} />;
  if (renderer === 'stats_summary') return <StatsSummary artifact={artifact} />;
  if (renderer === 'detail_card' || renderer === 'company_profile_card' || renderer === 'infrastructure_summary' || renderer === 'threat_model') return <DetailCard artifact={artifact} />;
  if (renderer === 'created_card') return <CreatedCard artifact={artifact} />;
  if (renderer === 'list' || renderer === 'jobs_list' || renderer === 'news_list') return <ListArtifact artifact={artifact} />;
  if (renderer === 'report_section') return <ReportSection artifact={artifact} />;
  if (renderer === 'grouped_lists') return <GroupedLists artifact={artifact} />;
  if (renderer === 'bar_chart') return <BarChartArtifact artifact={artifact} />;
  return <RawJsonArtifact artifact={artifact} />;
}

const parseSseChunk = (buffer) => {
  const events = [];
  const blocks = buffer.split(/\r?\n\r?\n/);
  const remainder = blocks.pop() || '';

  blocks.forEach((block) => {
    const lines = block.split(/\r?\n/);
    const parsed = { id: '', event: 'message', data: '' };
    lines.forEach((line) => {
      if (!line || line.startsWith(':')) return;
      if (line.startsWith('id:')) parsed.id = line.slice(3).trim();
      if (line.startsWith('event:')) parsed.event = line.slice(6).trim();
      if (line.startsWith('data:')) parsed.data += `${parsed.data ? '\n' : ''}${line.slice(5).trimStart()}`;
    });
    if (parsed.data) {
      try {
        const data = JSON.parse(parsed.data);
        events.push({
          id: Number(data.id || parsed.id) || Date.now(),
          event_type: data.event_type || parsed.event,
          payload: data.payload || data.data || data,
          created_at: data.created_at,
        });
      } catch (error) {
        events.push({
          id: Number(parsed.id) || Date.now(),
          event_type: parsed.event,
          payload: { message: parsed.data },
        });
      }
    }
  });

  return { events, remainder };
};

function Copilot() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [events, setEvents] = useState([]);
  const [timelineRuns, setTimelineRuns] = useState([]);
  const [liveTimelineRun, setLiveTimelineRun] = useState(null);
  const [, setArtifacts] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [machines, setMachines] = useState([]);
  const [sessionMachine, setSessionMachine] = useState(null);
  const [machineDraft, setMachineDraft] = useState({ machine_id: '', working_directory: '/home/kali', timeout_seconds: 120 });
  const [remoteCommands, setRemoteCommands] = useState([]);
  const [selectedRemoteCommand, setSelectedRemoteCommand] = useState(null);
  const [availableSkills, setAvailableSkills] = useState([]);
  const [sessionSkills, setSessionSkills] = useState([]);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [skillDraft, setSkillDraft] = useState(createEmptySkillDraft);
  const [skillEditorMode, setSkillEditorMode] = useState('');
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingSession, setLoadingSession] = useState(false);
  const [loadingMachines, setLoadingMachines] = useState(false);
  const [loadingSessionMachine, setLoadingSessionMachine] = useState(false);
  const [savingSessionMachine, setSavingSessionMachine] = useState(false);
  const [loadingRemoteCommands, setLoadingRemoteCommands] = useState(false);
  const [loadingRemoteCommandDetail, setLoadingRemoteCommandDetail] = useState(false);
  const [cancellingCommandId, setCancellingCommandId] = useState('');
  const [loadingSkills, setLoadingSkills] = useState(false);
  const [loadingSessionSkills, setLoadingSessionSkills] = useState(false);
  const [skillDetailLoading, setSkillDetailLoading] = useState(false);
  const [savingSkill, setSavingSkill] = useState(false);
  const [assigningSkillKey, setAssigningSkillKey] = useState('');
  const [removingAssignmentId, setRemovingAssignmentId] = useState('');
  const [deletingSkillId, setDeletingSkillId] = useState('');
  const [sending, setSending] = useState(false);
  const [busyActionId, setBusyActionId] = useState(null);
  const [error, setError] = useState('');
  const [connected, setConnected] = useState(false);
  const [activityDrawerOpen, setActivityDrawerOpen] = useState(false);
  const [skillsDrawerOpen, setSkillsDrawerOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [streamSessionId, setStreamSessionId] = useState(null);
  const streamAbortRef = useRef(null);
  const lastEventIdRef = useRef(0);
  const timelineRefreshTimerRef = useRef(null);
  const remoteCommandPollTimerRef = useRef(null);
  const terminalEventSeenRef = useRef(false);

  const request = useCallback(async (path, options = {}) => {
    const token = getToken();
    if (!token) {
      navigate('/');
      throw new Error('Missing authentication token.');
    }

    const response = await fetch(`${BASEURL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || data?.error) {
      throw new Error(data?.message || 'Copilot request failed.');
    }
    return data;
  }, [navigate]);

  const upsertArtifact = useCallback((artifact) => {
    if (!artifact) return;
    setArtifacts((prev) => {
      const id = artifact.id || `${artifact.artifact_type}-${artifact.title}`;
      const exists = prev.some((item) => (item.id || `${item.artifact_type}-${item.title}`) === id);
      if (exists) return prev.map((item) => ((item.id || `${item.artifact_type}-${item.title}`) === id ? artifact : item));
      return [artifact, ...prev];
    });
  }, []);

  const upsertTimelineEvent = useCallback((event) => {
    const item = getTimelineFromEvent(event);
    if (!item) return;

    const runId = item.run_id || `run-${event.id}`;
    const eventRun = {
      run_id: runId,
      title: item.title || 'Copilot Run',
      state: item.state || 'running',
      started_at: item.created_at,
      updated_at: item.created_at || new Date().toISOString(),
      items: [item],
    };

    setLiveTimelineRun((prev) => {
      if (prev && !isPendingRunId(prev.run_id) && String(prev.run_id) !== String(runId)) return prev;
      return mergeTimelineRun(prev, eventRun);
    });
    setTimelineRuns((prev) => {
      const existingRun = prev.find((run) => String(run.run_id) === String(runId));
      const rest = prev.filter((run) => String(run.run_id) !== String(runId));
      const nextRun = mergeTimelineRun(existingRun, eventRun);

      return [nextRun, ...rest].slice(0, 20);
    });
  }, []);

  const loadArtifacts = useCallback(async (sessionId) => {
    if (!sessionId) return;
    const data = await request(`/copilot/sessions/${sessionId}/artifacts`);
    setArtifacts(unwrapList(data, 'artifacts'));
  }, [request]);

  const loadTimeline = useCallback(async (sessionId = activeSessionId) => {
    if (!sessionId) return [];
    const data = await request(`/copilot/sessions/${sessionId}/timeline`);
    const runs = unwrapList(data, 'runs');
    setTimelineRuns(runs);
    return runs;
  }, [activeSessionId, request]);

  const loadPendingActions = useCallback(async (sessionId = activeSessionId) => {
    if (!sessionId) return [];
    const data = await request(`/copilot/sessions/${sessionId}/actions?status=pending`);
    const actions = unwrapList(data, 'actions');
    setPendingActions(actions);
    return actions;
  }, [activeSessionId, request]);

  const loadMachines = useCallback(async () => {
    setLoadingMachines(true);
    try {
      const nextMachines = await copilotRemoteApi.listMachines();
      setMachines(nextMachines);
      return nextMachines;
    } finally {
      setLoadingMachines(false);
    }
  }, []);

  const loadSessionMachine = useCallback(async (sessionId = activeSessionId) => {
    if (!sessionId) return null;
    setLoadingSessionMachine(true);
    try {
      const remoteMachine = await copilotRemoteApi.getSessionMachine(sessionId);
      setSessionMachine(remoteMachine);
      setMachineDraft({
        machine_id: remoteMachine?.machine_id || remoteMachine?.machine?.id || '',
        working_directory: remoteMachine?.working_directory || '/home/kali',
        timeout_seconds: remoteMachine?.timeout_seconds || 120,
      });
      return remoteMachine;
    } finally {
      setLoadingSessionMachine(false);
    }
  }, [activeSessionId]);

  const loadRemoteCommands = useCallback(async (sessionId = activeSessionId, { quiet = false } = {}) => {
    if (!sessionId) return [];
    if (!quiet) setLoadingRemoteCommands(true);
    try {
      const commands = await copilotRemoteApi.listSessionCommands(sessionId);
      const sorted = [...commands].sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
      setRemoteCommands(sorted);
      return sorted;
    } finally {
      if (!quiet) setLoadingRemoteCommands(false);
    }
  }, [activeSessionId]);

  const loadAvailableSkills = useCallback(async () => {
    setLoadingSkills(true);
    try {
      const skills = await copilotSkillsApi.listSkills();
      setAvailableSkills(skills);
      return skills;
    } finally {
      setLoadingSkills(false);
    }
  }, []);

  const loadSessionSkills = useCallback(async (sessionId = activeSessionId) => {
    if (!sessionId) return [];
    setLoadingSessionSkills(true);
    try {
      const skills = await copilotSkillsApi.listSessionSkills(sessionId);
      setSessionSkills(skills);
      return skills;
    } finally {
      setLoadingSessionSkills(false);
    }
  }, [activeSessionId]);

  const refreshSkills = useCallback(async (sessionId = activeSessionId) => {
    const results = await Promise.allSettled([
      loadAvailableSkills(),
      loadSessionSkills(sessionId),
    ]);
    const rejected = results.find((result) => result.status === 'rejected');
    if (rejected) {
      setError(rejected.reason?.message || 'Unable to refresh Copilot skills.');
    }
  }, [activeSessionId, loadAvailableSkills, loadSessionSkills]);

  const refreshPendingActions = useCallback((sessionId = activeSessionId) => {
    loadPendingActions(sessionId).catch((actionsError) => {
      setError(actionsError.message || 'Unable to load pending Copilot actions.');
    });
  }, [activeSessionId, loadPendingActions]);

  const refreshRemoteCommands = useCallback((sessionId = activeSessionId, options = {}) => {
    loadRemoteCommands(sessionId, options).catch((commandsError) => {
      setError(commandsError.message || 'Unable to load remote command jobs.');
    });
  }, [activeSessionId, loadRemoteCommands]);

  const scheduleTimelineRefresh = useCallback((sessionId = activeSessionId) => {
    if (!sessionId) return;
    if (timelineRefreshTimerRef.current) clearTimeout(timelineRefreshTimerRef.current);
    timelineRefreshTimerRef.current = setTimeout(() => {
      timelineRefreshTimerRef.current = null;
      loadTimeline(sessionId).catch(() => {
        // Timeline is history context; SSE remains the live source.
      });
    }, 350);
  }, [activeSessionId, loadTimeline]);

  const handleStreamEvent = useCallback((event) => {
    if (!event) return;
    lastEventIdRef.current = Math.max(lastEventIdRef.current, Number(event.id || 0));
    setEvents((prev) => {
      if (prev.some((item) => item.id === event.id && item.event_type === event.event_type)) return prev;
      return [...prev, event].slice(-120);
    });
    if (event.payload?.timeline || event.payload?.run_id) {
      upsertTimelineEvent(event);
    }

    if (
      ['confirmation_required', 'action_rejected'].includes(event.event_type) ||
      event.payload?.timeline?.state === 'waiting_for_approval'
    ) {
      terminalEventSeenRef.current = true;
      setIsProcessing(false);
      setLiveTimelineRun(null);
      refreshPendingActions();
      refreshRemoteCommands(activeSessionId, { quiet: true });
      scheduleTimelineRefresh();
    }

    if (event.event_type === 'action_approved') {
      refreshPendingActions();
      refreshRemoteCommands(activeSessionId, { quiet: true });
    }

    if (event.event_type === 'artifact_created') {
      const artifact = event.payload?.artifact || event.payload;
      upsertArtifact(artifact);
      refreshRemoteCommands(activeSessionId, { quiet: true });
    }

    if (['tool_started', 'tool_finished', 'tool_failed'].includes(event.event_type)) {
      refreshRemoteCommands(activeSessionId, { quiet: true });
    }

    if (event.event_type === 'assistant_message_final') {
      terminalEventSeenRef.current = true;
      setIsProcessing(false);
      setLiveTimelineRun(null);
      scheduleTimelineRefresh();
      const content = event.payload?.message || event.payload?.content || event.payload?.response || '';
      if (content) {
        setMessages((prev) => appendUniqueMessages(prev, [
          normalizeMessage({
            id: event.payload?.message_id || `assistant-${event.id}`,
            role: 'assistant',
            content,
          }),
        ]));
      }
    }

    if (event.event_type === 'error') {
      terminalEventSeenRef.current = true;
      setIsProcessing(false);
      setLiveTimelineRun(null);
      setError(event.payload?.message || 'The Copilot reported an error.');
      scheduleTimelineRefresh();
    }
  }, [activeSessionId, refreshPendingActions, refreshRemoteCommands, scheduleTimelineRefresh, upsertArtifact, upsertTimelineEvent]);

  const loadSession = useCallback(async (sessionId) => {
    if (!sessionId) return;
    setLoadingSession(true);
    setError('');
    try {
      const data = await request(`/copilot/sessions/${sessionId}`);
      const session = data.session || data;
      const returnedMessages = unwrapList(data, 'messages').length
        ? unwrapList(data, 'messages')
        : unwrapList(session, 'messages');
      const loadedEvents = unwrapList(data, 'events').length ? unwrapList(data, 'events') : unwrapList(session, 'events');
      setMessages(returnedMessages.map(normalizeMessage));
      setEvents(loadedEvents);
      lastEventIdRef.current = loadedEvents.reduce(
        (maxId, event) => Math.max(maxId, Number(event.id || 0)),
        0
      );
      await Promise.all([
        loadArtifacts(sessionId),
        loadPendingActions(sessionId),
        loadTimeline(sessionId),
        loadMachines(),
        loadSessionMachine(sessionId),
        loadRemoteCommands(sessionId),
        loadSessionSkills(sessionId),
      ]);
      setStreamSessionId(sessionId);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoadingSession(false);
    }
  }, [loadArtifacts, loadMachines, loadPendingActions, loadRemoteCommands, loadSessionMachine, loadSessionSkills, loadTimeline, request]);

  const loadSessions = useCallback(async () => {
    setLoadingSessions(true);
    setError('');
    try {
      const data = await request('/copilot/sessions');
      const nextSessions = unwrapList(data, 'sessions');
      setSessions(nextSessions);
      setActiveSessionId((current) => current || nextSessions[0]?.id || null);
      if (!nextSessions.length) {
        const created = await request('/copilot/sessions', {
          method: 'POST',
          body: JSON.stringify({ title: 'Pentest Copilot' }),
        });
        const session = created.session;
        setSessions([session]);
        setActiveSessionId(session.id);
      }
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoadingSessions(false);
    }
  }, [request]);

  const createSession = useCallback(async () => {
    setError('');
    try {
      const data = await request('/copilot/sessions', {
        method: 'POST',
        body: JSON.stringify({ title: 'Pentest Copilot' }),
      });
      const session = data.session;
      setSessions((prev) => [session, ...prev]);
      setActiveSessionId(session.id);
    } catch (createError) {
      setError(createError.message);
    }
  }, [request]);

  const openSkillsDrawer = useCallback(() => {
    setSkillsDrawerOpen(true);
    refreshSkills(activeSessionId).catch((skillsError) => {
      setError(skillsError.message || 'Unable to load Copilot skills.');
    });
  }, [activeSessionId, refreshSkills]);

  const viewSkill = useCallback(async (skill) => {
    if (!skill) return;
    setSkillDetailLoading(true);
    setSelectedSkill(normalizeSkillDetail(skill));
    try {
      const detail = isSystemSkill(skill)
        ? await copilotSkillsApi.getSystemSkill(skill.slug || getSkillKey(skill))
        : await copilotSkillsApi.getCustomSkill(skill.id || getSkillKey(skill));
      setSelectedSkill(normalizeSkillDetail(detail, skill));
    } catch (skillError) {
      setError(skillError.message || 'Unable to load skill details.');
    } finally {
      setSkillDetailLoading(false);
    }
  }, []);

  const startNewSkill = useCallback(() => {
    setSkillEditorMode('create');
    setSkillDraft(createEmptySkillDraft());
    setSelectedSkill(null);
  }, []);

  const startEditSkill = useCallback(async (skill) => {
    if (!skill?.editable || !isCustomSkill(skill)) return;
    setSkillEditorMode('edit');
    setSkillDetailLoading(true);
    try {
      const detail = await copilotSkillsApi.getCustomSkill(skill.id || getSkillKey(skill));
      const normalized = normalizeSkillDetail(detail, skill);
      setSelectedSkill(normalized);
      setSkillDraft({
        id: normalized.id || normalized.key,
        name: normalized.name || '',
        description: normalized.description || '',
        slug: normalized.slug || '',
        content: normalized.content || '',
      });
    } catch (skillError) {
      setError(skillError.message || 'Unable to load skill for editing.');
    } finally {
      setSkillDetailLoading(false);
    }
  }, []);

  const cancelSkillEdit = useCallback(() => {
    setSkillEditorMode('');
    setSkillDraft(createEmptySkillDraft());
  }, []);

  const saveSkill = useCallback(async () => {
    const payload = {
      name: skillDraft.name.trim(),
      description: skillDraft.description.trim(),
      content: skillDraft.content,
      ...(skillDraft.slug?.trim() ? { slug: skillDraft.slug.trim() } : {}),
    };
    if (!payload.name || !payload.content.trim()) return;

    setSavingSkill(true);
    setError('');
    try {
      const saved = skillEditorMode === 'edit'
        ? await copilotSkillsApi.updateCustomSkill(skillDraft.id, payload)
        : await copilotSkillsApi.createCustomSkill(payload);
      const normalized = normalizeSkillDetail(saved, { source: 'custom' });
      setSelectedSkill(normalized);
      setSkillDraft({
        id: normalized.id || normalized.key,
        name: normalized.name || payload.name,
        description: normalized.description || payload.description,
        slug: normalized.slug || payload.slug || '',
        content: normalized.content || payload.content,
      });
      setSkillEditorMode('');
      await refreshSkills(activeSessionId);
    } catch (skillError) {
      setError(skillError.message || 'Unable to save custom skill.');
    } finally {
      setSavingSkill(false);
    }
  }, [activeSessionId, refreshSkills, skillDraft, skillEditorMode]);

  const deleteSkill = useCallback(async (skill) => {
    if (!skill?.editable || !isCustomSkill(skill)) return;
    const skillId = skill.id || getSkillKey(skill);
    if (!skillId) return;
    if (!window.confirm(`Delete custom skill "${skill.name || skillId}"?`)) return;

    setDeletingSkillId(skillId);
    setError('');
    try {
      await copilotSkillsApi.deleteCustomSkill(skillId);
      if (String(selectedSkill?.id || selectedSkill?.key) === String(skillId)) {
        setSelectedSkill(null);
      }
      await refreshSkills(activeSessionId);
    } catch (skillError) {
      setError(skillError.message || 'Unable to delete custom skill.');
    } finally {
      setDeletingSkillId('');
    }
  }, [activeSessionId, refreshSkills, selectedSkill]);

  const assignSkill = useCallback(async (skill) => {
    if (!activeSessionId || !skill) return;
    const identity = getSkillIdentity(skill);
    setAssigningSkillKey(identity);
    setError('');
    try {
      await copilotSkillsApi.assignSessionSkill(activeSessionId, {
        skill_source: skill.source,
        skill_key: getSkillKey(skill),
      });
      await loadSessionSkills(activeSessionId);
    } catch (skillError) {
      setError(skillError.message || 'Unable to assign skill to this session.');
    } finally {
      setAssigningSkillKey('');
    }
  }, [activeSessionId, loadSessionSkills]);

  const removeSkill = useCallback(async (skill) => {
    if (!activeSessionId || !skill || skill.locked || isGeneralSkill(skill)) return;
    const assignmentId = skill.assignment_id;
    if (!assignmentId) return;

    setRemovingAssignmentId(assignmentId);
    setError('');
    try {
      await copilotSkillsApi.removeSessionSkill(activeSessionId, assignmentId);
      await loadSessionSkills(activeSessionId);
    } catch (skillError) {
      setError(skillError.message || 'Unable to remove skill from this session.');
    } finally {
      setRemovingAssignmentId('');
    }
  }, [activeSessionId, loadSessionSkills]);

  const saveSessionMachine = useCallback(async () => {
    if (!activeSessionId || !machineDraft.machine_id) return;
    const timeoutSeconds = Number(machineDraft.timeout_seconds) || 120;
    setSavingSessionMachine(true);
    setError('');
    try {
      await copilotRemoteApi.setSessionMachine(activeSessionId, {
        machine_id: machineDraft.machine_id,
        working_directory: machineDraft.working_directory || '/home/kali',
        timeout_seconds: timeoutSeconds,
      });
      await loadSessionMachine(activeSessionId);
    } catch (machineError) {
      setError(machineError.message || 'Unable to set the remote machine for this session.');
    } finally {
      setSavingSessionMachine(false);
    }
  }, [activeSessionId, loadSessionMachine, machineDraft]);

  const clearSessionMachine = useCallback(async () => {
    if (!activeSessionId || !sessionMachine) return;
    setSavingSessionMachine(true);
    setError('');
    try {
      await copilotRemoteApi.clearSessionMachine(activeSessionId);
      setSessionMachine(null);
      setMachineDraft({ machine_id: '', working_directory: '/home/kali', timeout_seconds: 120 });
    } catch (machineError) {
      setError(machineError.message || 'Unable to clear the remote machine for this session.');
    } finally {
      setSavingSessionMachine(false);
    }
  }, [activeSessionId, sessionMachine]);

  const openMachinesPage = useCallback(() => {
    navigate('/copilot/remote-machines');
  }, [navigate]);

  const openRemoteCommand = useCallback(async (commandId) => {
    if (!commandId) return;
    setLoadingRemoteCommandDetail(true);
    setError('');
    try {
      const command = await copilotRemoteApi.getCommand(commandId);
      setSelectedRemoteCommand(command);
    } catch (commandError) {
      setError(commandError.message || 'Unable to load remote command details.');
    } finally {
      setLoadingRemoteCommandDetail(false);
    }
  }, []);

  const cancelRemoteCommand = useCallback(async (commandId) => {
    if (!commandId) return;
    setCancellingCommandId(commandId);
    setError('');
    try {
      const command = await copilotRemoteApi.cancelCommand(commandId);
      setSelectedRemoteCommand((current) => (String(current?.id) === String(commandId) ? command : current));
      await loadRemoteCommands(activeSessionId, { quiet: true });
    } catch (commandError) {
      setError(commandError.message || 'Unable to cancel remote command.');
    } finally {
      setCancellingCommandId('');
    }
  }, [activeSessionId, loadRemoteCommands]);

  const hasActiveRemoteCommands = remoteCommands.some((command) =>
    ACTIVE_REMOTE_COMMAND_STATUSES.includes(String(command.status || '').toLowerCase())
  );

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => () => {
    if (timelineRefreshTimerRef.current) clearTimeout(timelineRefreshTimerRef.current);
    if (remoteCommandPollTimerRef.current) clearTimeout(remoteCommandPollTimerRef.current);
  }, []);

  useEffect(() => {
    if (!activeSessionId) return;
    lastEventIdRef.current = 0;
    setMessages([]);
    setEvents([]);
    setTimelineRuns([]);
    setLiveTimelineRun(null);
    setArtifacts([]);
    setPendingActions([]);
    setSessionMachine(null);
    setMachineDraft({ machine_id: '', working_directory: '/home/kali', timeout_seconds: 120 });
    setRemoteCommands([]);
    setSelectedRemoteCommand(null);
    setSessionSkills([]);
    setSelectedSkill(null);
    setSkillEditorMode('');
    setStreamSessionId(null);
    loadSession(activeSessionId);
  }, [activeSessionId, loadSession]);

  useEffect(() => {
    if (!activeSessionId || !hasActiveRemoteCommands) return undefined;
    remoteCommandPollTimerRef.current = setTimeout(() => {
      refreshRemoteCommands(activeSessionId, { quiet: true });
    }, 3500);

    return () => {
      if (remoteCommandPollTimerRef.current) clearTimeout(remoteCommandPollTimerRef.current);
    };
  }, [activeSessionId, hasActiveRemoteCommands, remoteCommands, refreshRemoteCommands]);

  useEffect(() => {
    if (!streamSessionId) return undefined;
    const token = getToken();
    if (!token) return undefined;

    let cancelled = false;
    let reconnectTimer = null;

    const connect = async () => {
      streamAbortRef.current?.abort();
      const abortController = new AbortController();
      streamAbortRef.current = abortController;

      try {
        setConnected(true);
        const response = await fetch(`${BASEURL}/copilot/sessions/${streamSessionId}/events?after_id=${lastEventIdRef.current}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          signal: abortController.signal,
        });

        if (!response.ok || !response.body) throw new Error('Event stream unavailable.');

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (!cancelled) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parsed = parseSseChunk(buffer);
          buffer = parsed.remainder;
          parsed.events.forEach(handleStreamEvent);
        }
        if (!cancelled) {
          reconnectTimer = setTimeout(connect, 1000);
        }
      } catch (streamError) {
        if (!cancelled && streamError.name !== 'AbortError') {
          setConnected(false);
          reconnectTimer = setTimeout(connect, 2500);
        }
      } finally {
        if (!cancelled) setConnected(false);
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      streamAbortRef.current?.abort();
      setConnected(false);
    };
  }, [handleStreamEvent, streamSessionId]);

  const sendMessage = async (message) => {
    if (!activeSessionId) return;
    setSending(true);
    setIsProcessing(true);
    setError('');
    terminalEventSeenRef.current = false;
    setLiveTimelineRun(createLiveRun({
      title: 'Working on request',
      itemTitle: 'Request sent',
      message: 'Waiting for Copilot to start streaming activity.',
    }));
    const optimistic = normalizeMessage({
      id: `user-${Date.now()}`,
      role: 'user',
      content: message,
    });
    setMessages((prev) => [...prev, optimistic]);
    try {
      const data = await request(`/copilot/sessions/${activeSessionId}/message`, {
        method: 'POST',
        body: JSON.stringify({ message }),
      });
      const returnedMessages = [
        data.user_message,
        data.assistant_message,
        ...(Array.isArray(data.messages) ? data.messages : []),
      ].filter(Boolean).map(normalizeMessage).filter((item) => {
        const isReturnedUserEcho = ['user', 'human'].includes(String(item.role || '').toLowerCase()) && normalizeMessageText(item.content) === normalizeMessageText(message);
        return !isReturnedUserEcho;
      });
      const responseEvents = extractStreamEvents(data);
      responseEvents.forEach(handleStreamEvent);
      if (returnedMessages.length) {
        setMessages((prev) => appendUniqueMessages(prev, returnedMessages));
      }
      await Promise.allSettled([
        loadPendingActions(activeSessionId),
        loadArtifacts(activeSessionId),
        loadRemoteCommands(activeSessionId, { quiet: true }),
      ]);
      if (
        returnedMessages.some((item) => item.content && item.role !== 'user') ||
        responseEvents.some((event) => ['assistant_message_final', 'error', 'confirmation_required'].includes(event.event_type))
      ) {
        setIsProcessing(false);
        setLiveTimelineRun(null);
        if (!terminalEventSeenRef.current) scheduleTimelineRefresh(activeSessionId);
      }
    } catch (sendError) {
      setIsProcessing(false);
      setLiveTimelineRun(null);
      setError(sendError.message);
    } finally {
      setSending(false);
    }
  };

  const resolveAction = async (actionId, decision) => {
    if (actionId === undefined || actionId === null || actionId === '') return;
    const action = pendingActions.find((item) => String(item.id) === String(actionId));
    const shellAction = isShellCommandAction(action);
    setBusyActionId(actionId);
    setIsProcessing(true);
    setError('');
    terminalEventSeenRef.current = false;
    setLiveTimelineRun(createLiveRun({
      runId: `approval-${actionId}-${Date.now()}`,
      title: shellAction ? 'Running remote command' : 'Running approved action',
      itemTitle: decision === 'reject' ? 'Action rejected' : 'Approval submitted',
      message: shellAction && decision === 'approve'
        ? 'Command approved. Waiting for the remote machine to run it.'
        : 'Copilot is continuing the approved workflow.',
      icon: 'approval',
      eventType: 'action_approved',
    }));
    try {
      await request(`/copilot/actions/${actionId}/${decision}`, { method: 'POST' });
      await Promise.allSettled([
        loadPendingActions(activeSessionId),
        loadRemoteCommands(activeSessionId, { quiet: true }),
      ]);
      if (decision === 'reject') {
        setIsProcessing(false);
        setLiveTimelineRun(null);
        if (!terminalEventSeenRef.current) scheduleTimelineRefresh(activeSessionId);
      }
    } catch (actionError) {
      setIsProcessing(false);
      setLiveTimelineRun(null);
      setError(actionError.message);
    } finally {
      setBusyActionId(null);
    }
  };

  if (!getToken()) return <AuthError message="Please sign in to use the Pentesting Copilot." />;

  return (
    <Box className="copilot-page">
      <Box className="copilot-command-header">
        <Box>
          <Typography className="copilot-eyebrow">AI Pentesting Agent</Typography>
          <Typography className="copilot-hero-title">Pentesting Copilot</Typography>
          <Typography className="copilot-hero-copy">
            Ask questions, inspect findings, create scans, and run approved actions from one focused workspace.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, justifyContent: 'flex-end' }}>
          <Chip icon={<BoltRoundedIcon />} label={connected ? 'Streaming live' : 'Ready'} className="copilot-hero-chip" />
          <Chip icon={<FactCheckOutlinedIcon />} label={`${pendingActions.length} pending`} className="copilot-hero-chip" />
          <Chip icon={<ExtensionRoundedIcon />} label={`${Math.max(sessionSkills.length, 1)} skills`} className="copilot-hero-chip" />
          <Chip icon={<HistoryRoundedIcon />} label={activeSessionId ? `Session #${activeSessionId}` : 'No session'} className="copilot-hero-chip" />
        </Stack>
      </Box>

      {error ? (
        <Alert severity="error" className="copilot-floating-alert" onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      <Grid container columnSpacing={2} rowSpacing={{ xs: 2, lg: 0 }} className="copilot-grid">
        <Grid item xs={12} lg={8.65}>
          {loadingSession ? (
            <Card className="copilot-panel copilot-loading-panel">
              <CircularProgress />
              <Typography sx={{ color: SLATE_TEXT }}>Loading Copilot session...</Typography>
            </Card>
          ) : (
            <ChatThread
              messages={messages}
              pendingActions={pendingActions}
              events={events}
              liveTimelineRun={liveTimelineRun}
              connected={connected}
              sessionSkills={sessionSkills}
              loadingSessionSkills={loadingSessionSkills}
              activityOpenCount={events.length}
              isProcessing={isProcessing}
              onOpenActivity={() => setActivityDrawerOpen(true)}
              onOpenSkills={openSkillsDrawer}
              onSend={sendMessage}
              sending={sending}
              onApprove={(id) => resolveAction(id, 'approve')}
              onReject={(id) => resolveAction(id, 'reject')}
              busyActionId={busyActionId}
            />
          )}
        </Grid>
        <Grid item xs={12} lg={3.35}>
          <WorkspacePanel
            connected={connected}
            sessions={sessions}
            activeSessionId={activeSessionId}
            loadingSessions={loadingSessions}
            sessionSkills={sessionSkills}
            loadingSessionSkills={loadingSessionSkills}
            machines={machines}
            sessionMachine={sessionMachine}
            machineDraft={machineDraft}
            loadingSessionMachine={loadingSessionMachine || loadingMachines}
            savingSessionMachine={savingSessionMachine}
            remoteCommands={remoteCommands}
            loadingRemoteCommands={loadingRemoteCommands}
            cancellingCommandId={cancellingCommandId}
            onSelectSession={setActiveSessionId}
            onNewSession={createSession}
            onRefreshSessions={loadSessions}
            onOpenSkills={openSkillsDrawer}
            onMachineDraftChange={setMachineDraft}
            onSaveSessionMachine={saveSessionMachine}
            onClearSessionMachine={clearSessionMachine}
            onOpenMachines={openMachinesPage}
            onRefreshRemoteCommands={() => refreshRemoteCommands(activeSessionId)}
            onOpenCommand={openRemoteCommand}
            onCancelCommand={cancelRemoteCommand}
          />
        </Grid>
      </Grid>
      <ActivityDrawer
        open={activityDrawerOpen}
        events={events}
        timelineRuns={timelineRuns}
        connected={connected}
        onClose={() => setActivityDrawerOpen(false)}
      />
      <SkillsDrawer
        open={skillsDrawerOpen}
        onClose={() => setSkillsDrawerOpen(false)}
        activeSessionId={activeSessionId}
        availableSkills={availableSkills}
        sessionSkills={sessionSkills}
        loadingSkills={loadingSkills}
        loadingSessionSkills={loadingSessionSkills}
        selectedSkill={selectedSkill}
        skillDetailLoading={skillDetailLoading}
        skillDraft={skillDraft}
        skillEditorMode={skillEditorMode}
        savingSkill={savingSkill}
        assigningSkillKey={assigningSkillKey}
        removingAssignmentId={removingAssignmentId}
        deletingSkillId={deletingSkillId}
        onRefresh={() => refreshSkills(activeSessionId)}
        onViewSkill={viewSkill}
        onNewSkill={startNewSkill}
        onEditSkill={startEditSkill}
        onDraftChange={setSkillDraft}
        onSaveSkill={saveSkill}
        onCancelEdit={cancelSkillEdit}
        onDeleteSkill={deleteSkill}
        onAssignSkill={assignSkill}
        onRemoveSkill={removeSkill}
      />
      <RemoteCommandDetailDrawer
        open={Boolean(selectedRemoteCommand) || loadingRemoteCommandDetail}
        command={selectedRemoteCommand}
        loading={loadingRemoteCommandDetail}
        onClose={() => setSelectedRemoteCommand(null)}
        onCancel={cancelRemoteCommand}
      />
    </Box>
  );
}

export default Copilot;
